import { App } from "./App.jsx";
import { AcceptInvite } from "./components/AcceptInvite.jsx";
import { Toast, useToast } from "./components/Common.jsx";
import { InstallExplainerCard, InstallInstructionModal, InvitationGate, Onboarding } from "./components/Onboarding.jsx";
import { apiGet, apiMintDeviceToken, apiRedeemHandover, getDeviceToken, apiSet, apiValidateInvite, apiVerifyJoinToken, apiVerifyPasscode, clearCachedHousehold, clearDeviceToken, DEVICE_TOKEN_KEY_PREFIX, HOUSEHOLD_CACHE_PREFIX, isNetworkError, readCachedHousehold, regenerateDeviceId, writeCachedHousehold, apiRequestJoin, apiJoinRequestStatus, apiRedeemJoinTicket, apiPeekInvite, apiAcceptInvite} from "./lib/api.js";
import { genPasscode, cleanOwnerName, CREATION_TICKET_KEY, CREATOR_KEY_PREFIX, DEVICE_CODE_KEY, DEVICE_ROLE_KEY_PREFIX, HIDDEN_CHILDREN_KEY_PREFIX, INVITE_OK_KEY, MUTED_CHILDREN_KEY_PREFIX, PENDING_DEVICE_SETUP_KEY_PREFIX, PRIVACY_VERSION, SEEN_CHILDREN_KEY_PREFIX, TERMS_VERSION, genCode, nowIso } from "./lib/constants.js";
import { dateStrAddDays, defaultState, migrateState, todayStr, weekStartOf } from "./lib/utils.js";
import { newHomeworkList } from "./lib/homework.js";
import { requestSharedListPass, setPendingSharedCode, sharedCodeFromUrl } from "./lib/homework-share.js";
import { HomeworkFork } from "./components/HomeworkFork.jsx";
import { newOwnerAdult } from "./lib/adults.js";
import { emptyGameState } from "./lib/adaptive.js";
import { applyVaultInterest } from "./lib/vaultInterest.js";
import { loadContentBundle, t as ct } from "./lib/content.js";
import { LandingPage } from "./components/Landing.jsx";
import { AppErrorBoundary } from "./components/AppErrorBoundary.jsx";
import { getAcquisition, setMetricsHousehold, track } from "./lib/metrics.js";
import { IS_NATIVE, apiUrl, useImageGrantRefresh } from "./lib/origin.js";
const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

// Consecutive 409-rebase-retry cycles before a device stops fighting and adopts the
// server copy (docs/SAVE_CONCURRENCY_SCOPE.md). Each cycle means another device won a
// save race during OUR retry — four in a row is pathological, not normal contention.
const MAX_CONFLICT_RETRIES = 4;

// Everything this boot's URL carries — invite codes, join tokens, one-time tickets, the
// install handover — read ONCE and stripped from the address bar in the same pass, so
// nothing lingers in browser history or a screenshot and a manual refresh can't re-trigger
// any of it.
//
// This used to live inside `inviteOk`'s state initializer, stashing what it found on a ref
// that three LATER initializers read. State initializers run in declaration order, so those
// three read the ref BEFORE the parse had written to it: `ticketJoin` and `adultInvite` were
// always null, and a device following an Add-a-device link or an emailed adult invite fell
// through to the ordinary Join screen — asking for a household code it was never given
// (Alan, staging, 19 Sep). A plain function that runs before the first useState is what makes
// that class of bug impossible rather than fixed: there is no longer an order in which one of
// these values can be read early.
function readEntryParams() {
  const entry = {
    invite: null,       // LB-94 shared invite code, prefilled into the gate
    qrJoin: null,       // quick-join QR: { code, token }
    ticketJoin: null,   // LB-341 Add-a-device ticket: { code, ticket }
    adultInvite: null,  // LB-342 emailed adult invite: { code, token }
    handover: null,     // install handover: { code, ticket }
    installPrompt: false,
    joinGone: null,     // "ticket" | "invite" — a /join/ link the worker could not resolve
    sharedList: null,   // LB-382: a shared practice list (HW-XXXXXX) opened with no household here
    inviteOk: false,
  };
  try {
    // BUG FIX: iOS gives every standalone (Home-Screen-launched) web app its own separate
    // localStorage sandbox from the Safari tab it was installed from, even for the exact
    // same origin. A household code written to localStorage moments earlier in the setup
    // wizard doesn't exist yet in that fresh sandbox, so the very first standalone launch
    // used to land straight back on the invitation gate — see InstallButton's iOS branch
    // (Onboarding.jsx) for where this gets written into the URL in the first place, right
    // before the person is sent off to do Share → Add to Home Screen in Safari's own UI.
    //
    // Consumed and stripped in the same pass: whether or not a code was found, the URL
    // never carries `?resume=` past this point, so it can't linger in the address bar,
    // browser history, or a screenshot of this screen, and a second boot (e.g. a manual
    // refresh) can't re-trigger this path.
    const params = new URLSearchParams(window.location.search);
    const resumeCode = params.get("resume");
    if (resumeCode) {
      if (!localStorage.getItem(DEVICE_CODE_KEY)) {
        localStorage.setItem(DEVICE_CODE_KEY, resumeCode.toUpperCase());
      }
      // Handover (Sep 2026): the URL may also carry a single-use ticket (see
      // worker/lib/handover.js). It is stashed here — NOT in localStorage, it must not
      // outlive this boot — and redeemed by the effect below before the household loads,
      // so this sandbox is a known, minted device from its very first read. Stripped from
      // the URL in the same pass as `resume`, for the same reasons.
      const ticket = params.get("t");
      if (ticket && !getDeviceToken(resumeCode.toUpperCase())) {
        entry.handover = { code: resumeCode.toUpperCase(), ticket };
      }
      params.delete("resume");
      params.delete("t");
    }
    // Invite-pass carry for a PRE-household iOS install (Aug 2026, install explainer):
    // the invitation-gate pass lives in Safari's localStorage, which — same sandbox
    // rule as the household code above — does NOT cross into the installed app. A
    // person who installs from the pitch screen (before any household exists, so
    // there's no code for `resume` to carry) gets `?resume-invite=1` baked into the
    // URL instead (InstallButton's iOS branch, Onboarding.jsx), and this honours it:
    // write the pass into the fresh sandbox so the icon's first launch lands on the
    // pitch screen, not back on the invitation gate. Consumed and stripped in the same
    // pass as everything else here, for the same address-bar/history/screenshot reasons.
    // install=1 (LB-294): the Install button reloaded the page into a carried URL so Safari
    // captured the parameterised manifest at load. Show the Add to Home Screen instruction
    // on this fresh page. Stashed in a ref, consumed by the render below, stripped with the
    // rest — a manual refresh must not re-show it.
    if (params.get("install") === "1") entry.installPrompt = true;
    params.delete("install");
    // LB-184: the carry is now the creation TICKET itself when the Safari tab held one, so the
    // installed app can create the household the invite paid for; the literal "1" (an icon
    // installed before this change, or a tab that had no ticket) still sets the pass alone.
    const resumeInvite = params.get("resume-invite");
    if (resumeInvite) {
      localStorage.setItem(INVITE_OK_KEY, "1");
      if (/^[A-Za-z0-9_-]{16,128}$/.test(resumeInvite)) localStorage.setItem(CREATION_TICKET_KEY, resumeInvite);
      params.delete("resume-invite");
    }
    // Quick-join QR: two params together, read once and stripped regardless of whether
    // this device ends up using them (e.g. it already has a household, or the code looks
    // malformed) — a scanned link is single-use-looking even though the token itself is
    // reusable, so a second page load never re-prompts from browser history.
    // Deletion confirmation return link (28 Aug, staging test G): the "Back to Little
    // Bankers" CTA on the deleted page carries ?deleted=CODE. Scoped to the code that
    // was actually deleted — a device belonging to some OTHER household must be able to
    // survive a stray or shared link untouched, so the saved code has to match before
    // anything is cleared. Consumed and stripped here with everything else, for the same
    // address-bar/history/screenshot reasons.
    const deletedCode = params.get("deleted");
    if (deletedCode) {
      const dead = deletedCode.toUpperCase();
      try {
        if ((localStorage.getItem(DEVICE_CODE_KEY) || "").toUpperCase() === dead) {
          localStorage.removeItem(DEVICE_CODE_KEY);
          localStorage.removeItem(HOUSEHOLD_CACHE_PREFIX + dead);
          localStorage.removeItem(DEVICE_TOKEN_KEY_PREFIX + dead);
          localStorage.removeItem(PENDING_DEVICE_SETUP_KEY_PREFIX + dead);
        }
      } catch {}
      params.delete("deleted");
    }
    // A /join/<code> link the worker could not resolve (dead ticket, spent or expired
    // invite, or something that was never either). It redirects here with this marker
    // rather than dropping the person on the code form, which is the one screen that
    // cannot help them: they were never given a household code. Same standing as a
    // well-formed-but-fake ?jointicket= link below — it opens a screen that says what
    // happened and offers the code form as the way out; it grants nothing.
    const gone = params.get("joingone");
    if (gone === "ticket" || gone === "invite") entry.joinGone = gone;
    params.delete("joingone");
    const qrCode = params.get("code");
    const qrToken = params.get("jointoken");
    // LB-341: a one-time Add-a-device ticket. Same link shape as the quick-join QR so this
    // one parser reads both, but it is NOT a credential on its own — presenting it parks a
    // request the issuing adult has to approve, so it can be eight readable characters.
    const qrTicket = params.get("jointicket");
    // LB-342: an emailed adult invite. Like the ticket link it is an invitation in itself,
    // so it passes the gate — but unlike every other join credential the person holding it
    // has no device here at all, which is what the accept flow exists to fix.
    const adultInvite = params.get("adultinvite");
    const validQrJoin = !!(qrCode && qrToken && /^[A-Za-z0-9]{6}$/.test(qrCode));
    const validTicketJoin = !!(qrCode && qrTicket && /^[A-Za-z0-9]{6}$/.test(qrCode) && /^[A-Za-z0-9-]{8,9}$/.test(qrTicket));
    if (validQrJoin) {
      entry.qrJoin = { code: qrCode.toUpperCase(), token: qrToken };
    }
    if (validTicketJoin) {
      entry.ticketJoin = { code: qrCode.toUpperCase(), ticket: qrTicket };
    }
    const validInvite = !!(qrCode && adultInvite && /^[A-Za-z0-9]{6}$/.test(qrCode) && adultInvite.length > 20);
    // LB-342: an install carry may name the step to resume at, so an adult who installed
    // mid-accept is not asked to tap Join again in the fresh storage jar. It selects a screen
    // and grants nothing; the token is still the only credential. Anything unrecognised is
    // dropped and the flow starts at the household screen.
    const inviteStep = params.get("invitestep");
    if (validInvite) entry.adultInvite = { code: qrCode.toUpperCase(), token: adultInvite, step: inviteStep === "pin" ? "pin" : null };
    params.delete("code");
    params.delete("jointoken");
    params.delete("jointicket");
    params.delete("adultinvite");
    params.delete("invitestep");
    // Shared invite link (LB-94): a link pasted into WhatsApp, iMessage or a text
    // arrives as `/?invite=CODE&utm_*`. The code is PREFILLED into the landing page's
    // invite box (LB-361; it was the gate under LB-94) and the visitor still taps Get
    // started — deliberately not auto-submitted, because
    // validateAndRedeemInvite spends a use at VALIDATION time (worker/lib/invites.js),
    // so auto-submitting would burn a use on a second open, a back-navigation, or a
    // bot that follows the link. The tap is what spends it.
    //
    // Same 4-10 char shape the worker accepts — custom-minted codes (including the
    // 5-char launch-weekend one) are not the generator's 6. A malformed value is
    // ignored and stripped rather than prefilled: the gate should look untouched, not
    // pre-filled with rubbish the person then has to clear.
    //
    // Stripped here with everything else so it can't linger in the address bar,
    // history or a screenshot. NOTE: `invite=` is deliberately NOT added to the
    // robots.txt Disallow list (worker/index.js) — a Disallow stops the fetch, and
    // several link unfurlers honour it, which would kill the very WhatsApp/iMessage
    // preview this link exists to produce. Stripping client-side is the protection.
    const sharedInvite = params.get("invite");
    if (sharedInvite && /^[A-Za-z0-9]{4,10}$/.test(sharedInvite)) {
      entry.invite = sharedInvite.toUpperCase();
    }
    params.delete("invite");
    if (resumeCode || resumeInvite || qrCode || qrToken || qrTicket || adultInvite || deletedCode || sharedInvite || entry.joinGone || entry.installPrompt) {
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
    // Devices that have already joined a household don't need to pass the invite
    // gate again — only new visitors with no saved household should see it. A device
    // arriving with a well-formed scanned quick-join link is, by definition, already
    // invited by whoever generated that QR — the household itself is the invitation.
    // (A malformed/garbage code+jointoken pair does NOT bypass the gate — only a link
    // shaped like a real one does.)
    if (localStorage.getItem(DEVICE_CODE_KEY)) { entry.inviteOk = true; return entry; }
    // LB-382 (Alan, 1 Oct): a shared practice list opened in a browser with NO household on it
    // — which is every iPhone link, because a link cannot open the installed app and the
    // browser does not share its storage. That visitor gets the fork (HomeworkFork), not the
    // marketing page with the list nowhere in sight. A browser that HAS a household returned
    // on the line above and never reaches here: App reads ?practice= itself and files the
    // request. The code is taken out of the address bar here for the reason App gives.
    {
      const listCode = sharedCodeFromUrl(window.location.search);
      if (listCode) {
        entry.sharedList = listCode;
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("practice");
          window.history.replaceState({}, "", url.pathname + (url.search || "") + url.hash);
        } catch { /* an address bar we cannot rewrite is not a reason to drop the list */ }
      }
    }
    if (validQrJoin) { entry.inviteOk = true; return entry; }
    // A ticket link is itself an invitation — whoever issued it is already in the
    // household — so it passes the gate for the same reason a quick-join QR does.
    if (validTicketJoin) { entry.inviteOk = true; return entry; }
    if (validInvite) { entry.inviteOk = true; return entry; }
    // A /join/ link the worker could not resolve. It gets the same standing as a
    // well-formed-but-fake ticket link above: it opens the screen that SAYS the code has run
    // out, with the code form offered as the way out. Dropping such a visitor on the landing
    // page instead would be the app blaming them for a link it issued.
    if (entry.joinGone) { entry.inviteOk = true; return entry; }
    entry.inviteOk = localStorage.getItem(INVITE_OK_KEY) === "1";
    return entry;
  } catch { return entry; }
}

// A device that has already answered "whose device is this?" — B4 of the adult-invite flow
// asks it in as many words — must not be sent through App's three-screen device setup to be
// asked again, with a stop in Kid Mode on the way. Recording the answer here is what makes
// the screenflow B2, B3, B4, Parent Mode. Mirrors applyDeviceRole in App.jsx: nobody is
// hidden or muted on a grown-up's or a shared device, and everyone present now counts as
// seen, because the new-child prompt is for arrivals after this point.
function rememberDeviceRole(code, role, profiles) {
  try {
    localStorage.setItem(DEVICE_ROLE_KEY_PREFIX + code, JSON.stringify(role));
    localStorage.setItem(HIDDEN_CHILDREN_KEY_PREFIX + code, "[]");
    localStorage.setItem(MUTED_CHILDREN_KEY_PREFIX + code, "[]");
    localStorage.setItem(SEEN_CHILDREN_KEY_PREFIX + code, JSON.stringify((profiles || []).map((p) => p.id)));
  } catch {}
}

export function Root() {
  // Everything the URL carried, read once, before the first useState below. See
  // readEntryParams above for why the order is load-bearing.
  const entryRef = useRef(null);
  if (!entryRef.current) entryRef.current = readEntryParams();
  const entry = entryRef.current;
  // A scanned QR (Aug 2026 quick-join) lands here as `?code=XXXXXX&jointoken=YYYY` — read
  // and stripped from the URL by readEntryParams. Unlike `resume`, this does NOT immediately
  // write to localStorage or invite itself into an existing household — it's just handed to
  // Onboarding as a "here's what was scanned, want to join?" prompt, so a stray/old QR link
  // never silently joins a device.
  const [pendingQrJoin, setPendingQrJoin] = useState(() => entry.qrJoin);
  // LB-341: a device that arrived on a ticket link. Three states, because the wait is the
  // feature: "asking" while the request is being parked, "waiting" while the issuing adult
  // has not answered, "gone" once the code is dead (cancelled, expired or already used) —
  // which is also where an unresolvable /join/ link lands.
  // LB-342 (B2, B3, B4): a device that arrived on an emailed invite.
  const [adultInvite, setAdultInvite] = useState(() => entry.adultInvite);
  const [invitePreview, setInvitePreview] = useState(null);
  // Shown once, after B4 — see acceptAdultInvite.
  const [offerInstall, setOfferInstall] = useState(false);
  const [inviteStage, setInviteStage] = useState(() => (entry.adultInvite ? "loading" : entry.joinGone === "invite" ? "gone" : null));
  const [ticketJoin, setTicketJoin] = useState(() => entry.ticketJoin);
  const [ticketJoinState, setTicketJoinState] = useState(() => (entry.ticketJoin ? "asking" : entry.joinGone === "ticket" ? "gone" : null));
  // Read by the ticket-join effect, which must not depend on the state it sets. A ref because
  // the effect needs the value at the moment it runs, not the value it closed over.
  const ticketJoinStateRef = useRef(ticketJoinState);
  ticketJoinStateRef.current = ticketJoinState;
  // What actually went wrong, for the "failed" screen. Shown, not swallowed: the settings
  // boundary already works this way, and the reason is the same — a person reporting a
  // failure with nothing to quote is reporting a feeling.
  const [ticketJoinDetail, setTicketJoinDetail] = useState(null);
  // What the retry button on the "failed" screen needs to try the same step again: the code
  // and the role, both already answered by the link or the invite, so the retry asks nothing.
  const joinAdoptRef = useRef(null);
  const [ticketJoinRetrying, setTicketJoinRetrying] = useState(false);
  // One join request per ticket, however many times this effect is re-evaluated.
  const startedJoinRef = useRef(null);
  // Handover: { code, ticket } captured from the URL on this boot, or null. A ref rather than
  // state because nothing renders it; it exists only to be consumed by the effect below.
  const pendingHandoverRef = useRef(entry.handover);
  const installPromptRef = useRef(entry.installPrompt); // install=1 on this boot
  const [inviteOk, setInviteOk] = useState(() => entry.inviteOk);
  // LB-382: the shared list this browser arrived with, until the visitor leaves the fork.
  const [sharedListCode, setSharedListCode] = useState(() => entry.sharedList);
  // LB-94/361: a shared link lands with the code already in the box.
  const [inviteInput, setInviteInput] = useState(() => entry.invite || "");
  const [inviteError, setInviteError] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  // LB-184: the gate is being shown again because a creation was refused for want of a live
  // ticket (expired after 7 days, or spent by the other sandbox of the same install).
  const [inviteExpired, setInviteExpired] = useState(false);
  // W3.5: fresh public visitors see the LANDING page; the invite-code entry is behind
  // its "I've got a code" CTA. showGate is view state only — the inviteOk pass itself
  // is untouched, so joined devices and invite-pass holders never see either screen.
  // LB-361 (Alan, 22 Sep) reverses LB-94's routing: a shared invite link opens the LANDING
  // page, with every waitlist ask swapped for the code and a Get started, not the bare
  // gate. Someone following a friend's link often does not know what Little Bankers is; the
  // gate told them nothing. The invite pass is untouched: `inviteOk` still flips only when
  // Get started validates the code, so a forwarded link grants nothing on its own.
  const [showGate, setShowGate] = useState(false);
  const [sharedInvite] = useState(() => !!entry.invite);
  // LB-95: set when someone leaves a code screen for the waitlist, so the landing page
  // opens with the signup in view instead of at the top of the pitch.
  const [focusWaitlist, setFocusWaitlist] = useState(false);
  // Landing → Join for a parent who already has a household (28 Aug, staging test C).
  // View state only, exactly like showGate: it opens the Join screen without touching the
  // invite pass, so nothing about who may CREATE a household changes.
  const [signInDirect, setSignInDirect] = useState(false);
  // Landing ↔ gate must behave like a navigation, not a state flip: opening the gate
  // pushes a history entry so the browser/phone back button returns to the landing
  // instead of leaving the site, and the gate's own "← Back" pops that same entry so
  // both routes stay consistent. popstate is the single source of "go back" truth.
  function openGate() {
    try { window.history.pushState({ lbGate: true }, ""); } catch {}
    setShowGate(true);
  }
  function closeGateViaHistory() {
    try {
      if (window.history.state && window.history.state.lbGate) { window.history.back(); return; }
    } catch {}
    setShowGate(false); // fallback if the entry is missing (storage-restricted browsers)
  }
  useEffect(() => {
    function onPop() { setShowGate(false); setSignInDirect(false); }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  // A shared link now arrives on the landing page itself (LB-361), which is the bottom of
  // the history stack like any other first visit, so there is no gate entry to push.
  useEffect(() => {
    if (!entryRef.current.invite) return; // through the ref: a mount-once effect naming `entry` would want it in the deps
    track("landing_cta", { kind: "invite_link" });
  }, []);
  // LB-95 — the code screens used to be a dead end for anyone who arrived without a
  // working code: the gate's only exit was Back, and Join needs a household code they
  // haven't got. This is the way out, to the one place that can actually help them.
  // It reuses the history pop rather than flipping view state directly, so the landing
  // page is reached the same way it is from Back and the two can't diverge.
  function goToWaitlist(from) {
    track("landing_cta", { kind: `waitlist_from_${from}` });
    setFocusWaitlist(true);
    closeGateViaHistory();
  }
  // The waitlist confirm route redirects back with ?waitlist=confirmed — one-shot banner.
  const [waitlistConfirmed] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("waitlist") === "confirmed"; } catch { return false; }
  });
  const [stage, setStage] = useState("checking");
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState(null);
  // Native Run A: photos in a native build render through a short-lived grant; this fetches
  // it as soon as the household is known and re-renders when it arrives. A no-op on the web.
  useImageGrantRefresh(code);
  const [state, setState] = useState(null);
  const [toast, showToast] = useToast();
  const [onboardError, setOnboardError] = useState("");
  const [pendingVerifiedPin, setPendingVerifiedPin] = useState(null);
  // Same shape as pendingVerifiedPin, for the household passcode: set right before a
  // deliberate passcode change so the next background save can prove to the server we
  // already knew the current value (see SettingsTab's confirmPasscodeChange), then
  // cleared once that save lands.
  const [pendingVerifiedPasscode, setPendingVerifiedPasscode] = useState(null);
  // Same shape again, for the quick-join token (Aug 2026) — set right before a deliberate
  // regenerate so the next background save can prove we knew the current value (see
  // SettingsTab's confirmRegenerateJoinToken), then cleared once that save lands.
  const [pendingVerifiedJoinToken, setPendingVerifiedJoinToken] = useState(null);
  // True only for the exact device/session that just created the household — this is what
  // lets App open straight into Parent Mode with no PIN for the guided setup wizard. A
  // device that joins an existing household (even one still mid-setup) always goes through
  // the normal PIN gate, so the no-PIN bypass never applies to anyone but the creator.
  const [justCreated, setJustCreated] = useState(false);
  const lastLocalEditRef = useRef(0);
  // W1.7 — offline support. `offline` drives the banner in App; `stateRef` mirrors state
  // every render (same pattern as Tests.jsx's onCooldownAtFinishRef) so the
  // foreground/online refetch — whose effect deps are [stage, code] — can push the
  // CURRENT state without a stale closure. `offlineDirtyRef` means "the user actually
  // edited on this device while a save couldn't land": the one condition under which
  // reconnecting pushes local state instead of pulling the server copy. Pull-first would
  // silently discard everything done on the plane; pushing an UNedited cache would just
  // as silently overwrite newer work from other devices — so both directions are guarded.
  // `offlineLoadAtRef` timestamps a load-from-cache so automatic state churn (the
  // autosave of the initial setState) can never count as a user edit.
  const [offline, setOffline] = useState(false);
  const stateRef = useRef(null);
  stateRef.current = state;
  const offlineDirtyRef = useRef(false);
  // Set by the foreground-refetch effect below; called by App's pull-to-refresh so the
  // gesture re-fetches household state instead of reloading the page (see PullToRefresh).
  const softRefreshRef = useRef(null);
  const offlineLoadAtRef = useRef(0);
  // ---- Save concurrency (docs/SAVE_CONCURRENCY_SCOPE.md) ----
  // The worker now rejects any save whose _meta.revision doesn't move the stored copy
  // FORWARD (409 + the current server copy in the body), so a stale device can no longer
  // silently erase another device's work. This block is the client half:
  //   - savedRevRef: highest revision known to be persisted server-side (or adopted FROM
  //     the server). The autosave effect skips any state at or below it — which also kills
  //     the old "echo" saves (load → setState → POST the same bytes back), a clobber
  //     vector of its own whenever the GET had been served a lagging KV edge read.
  //   - pendingOpsRef: an outbox of the update() mutator functions that produced every
  //     not-yet-confirmed revision. On a 409, adoptServerState() REPLAYS these onto the
  //     fresh server copy and resaves — so a kid's habit tick landing a moment after a
  //     parent's age edit keeps BOTH, invisibly, instead of one destroying the other.
  //     In-memory only by design: ops don't survive a reload (see the scope doc's
  //     accepted-corner note), the cached-snapshot push covers that path.
  //   - saveChainRef serialises saves: exactly one POST in flight, always of the CURRENT
  //     state — two of our own saves can never race each other into a self-conflict.
  const codeRef = useRef(null);
  codeRef.current = code;
  const verifiedRef = useRef({ pin: null, passcode: null, joinToken: null });
  verifiedRef.current = { pin: pendingVerifiedPin, passcode: pendingVerifiedPasscode, joinToken: pendingVerifiedJoinToken };
  const savedRevRef = useRef(0);
  const pendingOpsRef = useRef([]);
  const opSeqRef = useRef(0);
  const droppedOpIdsRef = useRef(new Set());
  const conflictRetriesRef = useRef(0);
  const saveChainRef = useRef(Promise.resolve());

  function resetSyncTracking(loadedState) {
    pendingOpsRef.current = [];
    droppedOpIdsRef.current = new Set();
    conflictRetriesRef.current = 0;
    savedRevRef.current = (loadedState && loadedState._meta && loadedState._meta.revision) || 0;
  }

  // Adopt a server copy as the new base, then replay any pending local ops on top.
  // Used by the passive refetch (so a foreground pull can no longer discard an edit
  // that hadn't saved yet) and by 409 conflict handling (the invisible merge). An op
  // whose replay throws is DROPPED (its target changed or vanished server-side) and its
  // id recorded for updateAndSave callers; an op that returns null became a no-op
  // against the fresh state (already settled elsewhere) and is simply retired.
  function adoptServerState(serverJson) {
    let fresh;
    try {
      fresh = migrateState(typeof serverJson === "string" ? JSON.parse(serverJson) : serverJson);
    } catch {
      return { ok: false, dropped: 0 };
    }
    const baseRev = (fresh._meta && fresh._meta.revision) || 0;
    savedRevRef.current = baseRev;
    let rev = baseRev;
    // Interest must bump the revision itself now: under echo suppression + the server's
    // revision gate, a state change that doesn't advance the revision never persists.
    if (applyVaultInterest(fresh)) rev += 1;
    let working = fresh;
    const survivors = [];
    let dropped = 0;
    for (const op of pendingOpsRef.current) {
      try {
        const next = op.fn(JSON.parse(JSON.stringify(working)));
        if (next === null) continue;
        rev += 1;
        working = next;
        survivors.push({ id: op.id, rev, fn: op.fn });
      } catch {
        dropped += 1;
        droppedOpIdsRef.current.add(op.id);
      }
    }
    pendingOpsRef.current = survivors;
    working._meta = rev !== baseRev
      ? { revision: rev, lastModified: nowIso() }
      : (fresh._meta || { revision: baseRev, lastModified: nowIso() });
    setState(working);
    stateRef.current = working;
    if (codeRef.current) writeCachedHousehold(codeRef.current, working);
    return { ok: true, dropped, advanced: rev !== baseRev };
  }

  // One save at a time, always of the CURRENT state. Every caller gets a promise for
  // its own link in the chain; a burst of updates collapses naturally because each link
  // re-reads stateRef and skips when its revision is already covered.
  function scheduleSave() {
    const link = saveChainRef.current.then(runSave, runSave);
    saveChainRef.current = link.then(() => {}, () => {});
    return link;
  }

  async function runSave() {
    const snap = stateRef.current;
    const c = codeRef.current;
    if (!snap || !c) return { ok: false, skipped: true };
    const rev = (snap._meta && snap._meta.revision) || 0;
    if (rev <= savedRevRef.current) return { ok: true, rev, skipped: true };
    const v = verifiedRef.current;
    try {
      await apiSet(c, snap, v.pin, v.passcode, v.joinToken);
      savedRevRef.current = Math.max(savedRevRef.current, rev);
      pendingOpsRef.current = pendingOpsRef.current.filter((op) => op.rev > rev);
      conflictRetriesRef.current = 0;
      setPendingVerifiedPin(null); setPendingVerifiedPasscode(null); setPendingVerifiedJoinToken(null);
      writeCachedHousehold(c, snap);
      offlineDirtyRef.current = false;
      setOffline(false);
      return { ok: true, rev };
    } catch (err) {
      if (err && err.revoked) { forceLeaveRevoked(); return { ok: false, rev, revoked: true }; }
      if (err && err.conflict) {
        conflictRetriesRef.current += 1;
        if (conflictRetriesRef.current > MAX_CONFLICT_RETRIES || !err.serverValue) {
          // Repeated conflicts mean we keep losing the race (or the 409 carried no state
          // to rebase onto) — stop fighting: adopt the server copy, drop what's left in
          // the outbox, and say so once. Rare by construction; honest when it happens.
          for (const op of pendingOpsRef.current) droppedOpIdsRef.current.add(op.id);
          pendingOpsRef.current = [];
          if (err.serverValue) adoptServerState(err.serverValue);
          conflictRetriesRef.current = 0;
          showToast(ct("sync.conflict_dropped", "Another device saved changes at the same time — this device has been updated with the latest."), "info");
          return { ok: false, rev, conflict: true, gaveUp: true };
        }
        const res = adoptServerState(err.serverValue);
        if (res.ok && res.dropped > 0) {
          showToast(ct("sync.conflict_dropped", "Another device saved changes at the same time — this device has been updated with the latest."), "info");
        }
        // A clean adoption with nothing left to push isn't a "retry" — reset the counter.
        if (res.ok && !res.advanced) conflictRetriesRef.current = 0;
        // If ops survived, adoptServerState's setState re-fires the autosave effect,
        // which schedules the retry — nothing more to do here.
        return { ok: false, rev, conflict: true, rebased: res.ok };
      }
      // W1.7: dirty means "a REAL user edit couldn't be saved" — gated on update()'s own
      // timestamp being newer than any load-from-cache, so the automatic autosave of a
      // freshly cache-loaded state can never mark this device dirty and later push stale
      // data over other devices' newer work.
      if (lastLocalEditRef.current > 0 && lastLocalEditRef.current >= offlineLoadAtRef.current) offlineDirtyRef.current = true;
      if (isNetworkError(err)) setOffline(true);
      return { ok: false, rev, err };
    }
  }

  // Server-issued invite codes (review 1.1b/P4): the gate UI is unchanged, only the
  // check moved server-side. On ok, INVITE_OK_KEY is set exactly as before — the pass
  // mechanism itself never changed.
  /* A device that has actually got INTO a household must stop being treated as a stranger.
   *
   * STAGING BUG 2 (30 Aug 2026): the landing page's "I've already got a code" route renders
   * Onboarding WITHOUT setting the invite pass — deliberately, since code + passcode is a
   * stronger proof than an invite code. But the whole gate lives behind `if (!inviteOk)`
   * at the top of render, and neither handleJoin, handleStart nor handleRestore ever
   * touched inviteOk. So a join could succeed completely — state loaded, stage "ready",
   * device token minted — and the screen would keep rendering the Onboarding form, because
   * render never got past the gate. From the outside the Join and Create buttons simply
   * looked dead.
   *
   * Flipping the pass on success is right rather than expedient: every caller has already
   * proved a stronger claim than an invite code (a passcode, a join token, a backup file,
   * or a creation that itself happened behind the gate).
   */
  function markInvitePassed() {
    try { localStorage.setItem(INVITE_OK_KEY, "1"); } catch {}
    setInviteOk(true);
    setSignInDirect(false);
    setShowGate(false);
  }

  // LB-382 (Alan, 1 Oct: "let them past the gate"). A family arriving from a shared practice
  // list is let in on the strength of the list, exactly as submitInvite lets one in on the
  // strength of a code: the worker hands back the same creation ticket, and it is stored in the
  // same place for the save that creates the household to spend. The list's code is left for
  // App to pick up once the household exists, so it is waiting in Requests after set-up.
  // Returns "ok" | "full" | "limited" | "error" for the fork to word.
  async function passGateWithSharedList(listCode) {
    const res = await requestSharedListPass(listCode);
    if (!res.ticket) return res.full ? "full" : res.limited ? "limited" : "error";
    try {
      localStorage.setItem(INVITE_OK_KEY, "1");
      localStorage.setItem(CREATION_TICKET_KEY, res.ticket);
    } catch {}
    setPendingSharedCode(listCode);
    setInviteExpired(false);
    setInviteOk(true);
    setSharedListCode(null);
    return "ok";
  }

  async function submitInvite() {
    setInviteBusy(true);
    setInviteError("");
    try {
      // LB-184: the redeem now returns a creation ticket as well as the pass. The pass still
      // drives the gate UI exactly as before; the ticket is what the worker requires on the
      // save that creates the household, and it is stored here until that save spends it.
      const ticket = await apiValidateInvite(inviteInput.trim());
      try {
        localStorage.setItem(INVITE_OK_KEY, "1");
        if (ticket) localStorage.setItem(CREATION_TICKET_KEY, ticket);
      } catch {}
      setInviteExpired(false);
      setInviteOk(true);
    } catch (err) {
      setInviteError(err.message || "That invitation code isn't right — check it and try again.");
    } finally {
      setInviteBusy(false);
    }
  }

  // Called any time apiGet/apiSet reports this device has been removed from its household
  // (Parent Mode → Settings → Devices → Remove, on some other device). Unlinks this device
  // exactly like onLeaveHousehold, but with a message that explains what happened rather
  // than the ordinary "couldn't load" error — this is a deliberate removal, not a network
  // hiccup or a bad code.
  function forceLeaveRevoked() {
    try { if (codeRef.current) clearDeviceToken(codeRef.current); } catch {}
    // W1.7: a removed device must not keep a readable copy of the household it was
    // removed from — wipe the offline cache along with the code.
    const c = code || (() => { try { return localStorage.getItem(DEVICE_CODE_KEY); } catch { return null; } })();
    if (c) clearCachedHousehold(c);
    try { localStorage.removeItem(DEVICE_CODE_KEY); } catch {}
    setCode(null);
    setState(null);
    setOnboardError("This device was removed from the household. Enter the household code and passcode to rejoin.");
    setStage("onboard");
  }

  // Household is gone for good — deleted by its owner, an admin, or the test sweep. Same
  // teardown as forceLeaveRevoked (code, cache and device token all cleared) but a
  // different message on purpose: revocation invites a rejoin, and here there is nothing
  // left to rejoin. Also the landing point for the ?deleted=CODE link on the deletion
  // confirmation page, which is why the code is passed in rather than read from `code` —
  // that link is usually opened from an email client, on a boot where state isn't set yet.
  function forceUnlinkDeleted(deadCode) {
    const c = deadCode || code || (() => { try { return localStorage.getItem(DEVICE_CODE_KEY); } catch { return null; } })();
    if (c) {
      try { clearDeviceToken(c); } catch {}
      clearCachedHousehold(c);
      try { localStorage.removeItem(PENDING_DEVICE_SETUP_KEY_PREFIX + c); } catch {}
    }
    try { localStorage.removeItem(DEVICE_CODE_KEY); } catch {}
    setCode(null);
    setState(null);
    setOnboardError(ct("onboarding.household_deleted", "That household has been deleted. There's nothing left to open — you can start a new household, or join a different one with its code and passcode."));
    setStage("onboard");
  }

  useEffect(() => {
    if (!inviteOk) return;
    const saved = localStorage.getItem(DEVICE_CODE_KEY);
    if (!saved) { setStage("onboard"); return; }
    (async () => {
      try {
        // Handover redeem happens FIRST, so the read below already carries a token. Failure is
        // non-fatal by design: a dead ticket (expired, already used, wrong household) just
        // leaves this device where the code-only carry always left it — with the household
        // known and the join screen one field away. The ticket is consumed server-side
        // whether or not the mint succeeds, so it is never worth a second try.
        const pending = pendingHandoverRef.current;
        if (pending && pending.code === saved) {
          pendingHandoverRef.current = null;
          // KV is eventually consistent: the ticket was written seconds ago from the Safari
          // tab and this read, from the installed app, may not see it yet. Alan hit exactly
          // this on staging — ticket in the URL, "household gone" on first launch. Retry on
          // a miss with a short backoff (about ten seconds in all) before giving up; a truly
          // dead ticket costs nothing extra because a miss never counts as a passcode failure.
          const waits = [0, 1500, 3000, 6000];
          for (const ms of waits) {
            if (ms) await new Promise((r) => setTimeout(r, ms));
            const r = await apiRedeemHandover(saved, pending.ticket);
            if (r.ok || !r.retry) break;
          }
        }
        const value = await apiGet(saved);
        if (!value) {
          // No stored state for a code this device has saved. This used to fall through
          // to defaultState() — which is how a deleted household came back: the app
          // rebuilt a fresh default under the very code the owner had just erased, and
          // the autosave effect wrote it straight back to KV (reported 28 Aug, staging
          // test G). There is no legitimate path here: handleStart writes the code to
          // localStorage only AFTER apiSet has stored the state, and handleJoin gates on
          // verify-passcode, which a codeless household can never satisfy. So an empty
          // read means the household is gone — admin delete, the CI sweep, or the owner's
          // own deletion loop — and the honest response is to unlink and say so, not to
          // manufacture a replacement.
          forceUnlinkDeleted(saved);
          return;
        }
        const loaded = migrateState(JSON.parse(value));
        resetSyncTracking(loaded);
        // Money Monday lazy accrual (lib/vaultInterest.js) — settle any Mondays that have
        // passed since this household's data was last touched, right as it loads. Now
        // bumps the revision itself when it actually paid: under echo suppression + the
        // server's revision gate, a change that doesn't advance the revision would never
        // persist. The background-save effect below still does the actual write.
        if (applyVaultInterest(loaded)) loaded._meta = { revision: savedRevRef.current + 1, lastModified: nowIso() };
        writeCachedHousehold(saved, loaded); // W1.7 — every successful load refreshes the offline copy
        setOffline(false);
        setState(loaded);
        setCode(saved);
        setStage("ready");
      } catch (err) {
        if (err && err.revoked) { forceLeaveRevoked(); return; }
        if (err && err.gone) { forceUnlinkDeleted(saved); return; }
        // W1.7 (Alan, from a plane, Aug 2026): a fetch that never reached the server used
        // to surface here raw ("Couldn't load your household: Load failed"). If this
        // device has EVER synced this household, open to the cached copy instead, with the
        // offline banner up. Deliberately NO applyVaultInterest on the cached copy: the
        // cache may be stale, accrual done here couldn't persist anyway, and it must not
        // manufacture state churn that could later be pushed over fresher server data —
        // interest settles properly on the next ONLINE load, exactly as if the app hadn't
        // been opened. Server-reachable failures (bad code, 4xx/5xx) keep today's path.
        const cached = isNetworkError(err) ? readCachedHousehold(saved) : null;
        if (cached) {
          const loaded = migrateState(cached.state);
          // The cached copy counts as "already persisted" for echo suppression — an
          // untouched cache load must never push; a real edit bumps past this and can.
          resetSyncTracking(loaded);
          offlineLoadAtRef.current = Date.now();
          setOffline(true);
          setState(loaded);
          setCode(saved);
          setStage("ready");
          return;
        }
        setOnboardError(isNetworkError(err)
          ? ct("offline.no_cache_error", "You're offline, and this device doesn't have a saved copy of your household yet. Reconnect to the internet and try again.")
          : `Couldn't load your household: ${err.message}`);
        setStage("onboard");
      }
    })();
  }, [inviteOk]);

  // Content Studio bundle (docs/CMS_SCOPE.md §1) — fetched once on mount, independent
  // of household state/auth, since it's public and applies to every visitor equally.
  // loadContentBundle() never throws: a failed fetch just leaves every ct()/t() call
  // resolving to its baked-in fallback string, which is exactly today's behaviour.
  // Not re-fetched on inviteOk/stage changes — one load per app session is enough;
  // a new publish is picked up on the visitor's next fresh load.
  useEffect(() => {
    loadContentBundle();
  }, []);

  useEffect(() => {
    if (stage !== "ready" || !state || !code) return;
    // All the actual save/retry/conflict logic lives in runSave()/adoptServerState()
    // above — this effect just schedules a link in the single-flight chain. States that
    // don't advance past savedRevRef (echoes of what was just loaded or adopted) are
    // skipped inside runSave, so a plain load or refetch no longer POSTs at all.
    scheduleSave();
  }, [state, stage, code]);

  useEffect(() => {
    if (stage !== "ready" || !code) return;
    // Passive freshness fallback: an installed PWA can sit backgrounded for a long time
    // with no way to know another device changed things, so pull the latest household
    // data whenever this instance comes back to the foreground. Silent on failure —
    // just keep whatever's already on screen rather than show an error for a background check.
    // This is also the main way a *removed* device notices — see forceLeaveRevoked().
    //
    // Guarded against a recent local edit: mobile keyboards and camera/photo pickers can
    // trigger a visibilitychange within a second or two of typing something (e.g. saving a
    // child's age or renaming them). If we refetch right then, we can race the just-issued
    // save and overwrite the edit on screen with the pre-edit server copy. Skip the refetch
    // for a few seconds after any local change so the save has time to land first.
    // Published to softRefreshRef below so pull-to-refresh can call it. The ref (rather
    // than a useCallback) keeps this function exactly where it is, inside the effect that
    // owns `code`/`stage`, so none of the save-concurrency reasoning above moves.
    function refetch() {
      // W1.7 — push-first when offline edits are pending: this device was actively used
      // while disconnected, so ITS state is the one to keep (the same last-write-wins the
      // app already applies between devices). A plain pull here would replace everything
      // done on the plane with the pre-flight server copy.
      if (offlineDirtyRef.current && stateRef.current) {
        // Push through the same single-flight chain as every other save — so if another
        // device DID write while this one was offline, the 409 path rebases the plane
        // edits onto the newer copy instead of either side silently losing.
        scheduleSave().then((res) => {
          if (res && res.ok && !res.skipped) {
            showToast(ct("offline.back_online_saved", "Back online. Your changes have been saved."), "good");
          }
        });
        return;
      }
      if (Date.now() - lastLocalEditRef.current < 4000) return;
      apiGet(code)
        .then((value) => {
          if (!value) return;
          // adoptServerState handles everything the old inline version did (migrate,
          // Money Monday settle-up, cache refresh) AND replays any pending local ops on
          // top — a foreground pull can no longer discard an edit that hadn't saved yet.
          adoptServerState(value);
          setOffline(false);
        })
        .catch((err) => {
          if (err && err.revoked) { forceLeaveRevoked(); return; }
          if (isNetworkError(err)) setOffline(true); // W1.7 — connectivity dropped mid-session
        });
    }
    softRefreshRef.current = refetch;
    function handleVisibility() {
      if (document.visibilityState !== "visible") return;
      refetch();
    }
    // Android WebAPKs (installed PWAs) are less consistent about firing visibilitychange on
    // resume than iOS Safari or desktop browsers — listening for focus too, same
    // belt-and-suspenders approach as the SW-update check in index.html.
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", refetch);
    // A device left open learns nothing. Until 20 Sep the three triggers below were the only
    // ones, and all three need somebody to LEAVE and come back — so a laptop sitting focused
    // while a parent approved on their phone kept showing the copy it fetched when it was
    // last switched to (Alan). The case that matters more is the child's: the family iPad is
    // open in front of them, the approval lands on a parent's phone, and ChildView's
    // already-built celebration never fires, because it watches `requests` and `requests`
    // never changes. Nothing was broken there; it was never told.
    //
    // 30s while visible, nothing while hidden — a backgrounded tab has visibilitychange to
    // catch it up, so polling one would be paying for an answer already coming free.
    // `refetch` keeps its own guards: it skips within 4s of a local edit and pushes instead
    // of pulling when offline edits are pending, so this cannot overwrite anything unsaved.
    let statePoll = setInterval(() => { if (document.visibilityState === "visible") refetch(); }, 30000);
    // W1.7 — connectivity returning is the third trigger: on a plane the app is usually
    // foregrounded the whole time, so visibility/focus never fire when data comes back.
    window.addEventListener("online", refetch);
    return () => {
      softRefreshRef.current = null;
      if (statePoll) { clearInterval(statePoll); statePoll = null; }
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", refetch);
      window.removeEventListener("online", refetch);
    };
  }, [stage, code]);

  // W2.5: metrics BOOT happens in main.jsx (once per page load, every route — see the
  // comment there for the W1.10 preview lesson). Root only attaches/detaches the
  // household code so the worker can derive the salted household hash.
  useEffect(() => {
    setMetricsHousehold(code || null);
  }, [code]);

  // LB-184: the worker refused to CREATE because this device holds no live creation ticket —
  // it expired (7 days), or the other sandbox of the same iOS install already spent it. Both
  // keys go, and the gate comes back with one line saying why. The invite use was consumed at
  // validation, so the parent needs a code that still has uses; the gate's waitlist link is
  // the way to one if theirs is spent. Not blamed: the copy states what happened, not whose
  // fault it was.
  function readCreationTicket() {
    try { return localStorage.getItem(CREATION_TICKET_KEY) || null; } catch { return null; }
  }
  function clearCreationTicket() {
    try { localStorage.removeItem(CREATION_TICKET_KEY); } catch {}
  }
  function onInviteRequired() {
    clearCreationTicket();
    try { localStorage.removeItem(INVITE_OK_KEY); } catch {}
    setInviteError("");
    setInviteExpired(true);
    setSignInDirect(false);
    setShowGate(true);
    setInviteOk(false);
    setStage("onboard");
  }

  // W5 — restore-into-NEW-household (P7, final): an exported backup file becomes a
  // brand-new household — new code, freshly generated passcode and quick-join token,
  // empty device registry — never a way into the original. Regenerating the
  // credentials is the security half of the decision: a backup file that leaks (old
  // laptop, shared drive) must not carry keys to ANY live household, including the new
  // one (its fresh passcode has never been in any file). ownerEmail is kept but reset
  // to unverified, so the standard create-time verification loop re-proves the inbox
  // before the new code is ever emailed anywhere.
  async function handleRestore(file) {
    setBusy(true); setOnboardError("");
    try {
      const text = await file.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { throw new Error("That file isn't a Little Bankers backup."); }
      const rawState = parsed && parsed.littleBankersBackup ? parsed.state : parsed;
      if (!rawState || !Array.isArray(rawState.profiles)) throw new Error("That file isn't a Little Bankers backup.");
      regenerateDeviceId();
      const newCode = genCode();
      // Restore bug (found 7 Sep restoring the demo household): migrateState takes an OBJECT —
      // every other caller parses first — but this passed a string, so `parsed.settings` was
      // undefined and the restore died on "reading 'vaultInterest'". Deep-copy the object.
      const restored = migrateState(JSON.parse(JSON.stringify(rawState)));
      delete restored._meta;
      restored.settings.householdPasscode = genPasscode();
      restored.settings.householdJoinToken = null; // re-minted on first QR reveal
      restored.ownerEmailVerified = false;
      restored.restoredAt = new Date().toISOString();
      // LB-340: a restore CREATES a household, so — exactly like handleStart below — it has
      // to build the Owner row itself. The worker's migration only runs on a READ, and the
      // save path now DELETES settings.parentPin; a restore posts straight to a fresh code
      // and never reads back, so without this the backup's PIN is dropped on the way in and
      // the household comes back on the "123456" placeholder. That is a restored household
      // anyone could walk into Parent Mode on, with nothing on screen to say the PIN had
      // changed. Carrying the file's PIN is safe for the same reason newOwnerAdult is: at
      // creation there is no stored household to overwrite, and the backup already holds
      // the PIN in plain sight. A post-LB-339 backup arrives with its own adults list and
      // is left exactly as it is.
      if (!Array.isArray(restored.adults) || restored.adults.length === 0) {
        const owner = newOwnerAdult({ name: restored.ownerName, email: restored.ownerEmail });
        const filePin = typeof restored.settings.parentPin === "string" ? restored.settings.parentPin : "";
        if (filePin) owner.pin = filePin;
        restored.adults = [owner];
      }
      if (!restored.settings.ownerAdultId) restored.settings.ownerAdultId = restored.adults[0].id;
      // LB-184: a restore CREATES a household (fresh code), so it presents the creation ticket
      // like any other creation — decision 1 in PATCH_C_SPEC.md. Spent on success.
      await apiSet(newCode, restored, null, null, null, { creationTicket: readCreationTicket() });
      clearCreationTicket();
      resetSyncTracking(restored);
      // LB-245: awaited, for the same reason as handleStart's mint below — a restore is a
      // creation, and the saves after it are gated.
      await apiMintDeviceToken(newCode, { passcode: restored.settings.householdPasscode }).catch(() => false);
      localStorage.setItem(DEVICE_CODE_KEY, newCode);
      try { localStorage.setItem(CREATOR_KEY_PREFIX + newCode, "1"); } catch {}
      setJustCreated(true);
      markInvitePassed();
      setCode(newCode); setState(restored); setStage("ready");
      track("household_restored", {});
      if (restored.ownerEmail) {
        fetch(apiUrl(`/api/household/${newCode}/welcome-email`), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: restored.ownerEmail }),
        }).catch(() => {});
      }
    } catch (err) {
      if (err && err.inviteRequired) { onInviteRequired(); return; }
      setOnboardError(err.message || "Couldn't restore that backup.");
      setStage("onboard");
    } finally {
      setBusy(false);
    }
  }

  async function handleStart(ownerEmail, ownerName = null) {
    setBusy(true); setOnboardError("");
    try {
      // Fresh device identity for a fresh household — see regenerateDeviceId() for why:
      // mainly relevant on the join path below, but applied here too so a device's
      // identity always resets on a deliberate "start over" action rather than only some.
      regenerateDeviceId();
      // Tombstoned codes are retired permanently (worker: `deleted:{code}`), so a fresh
      // genCode() can in principle collide with one and be refused. At 36^6 that is
      // vanishingly rare — but "vanishingly rare" is not "handled", and the failure mode
      // would be a parent unable to create a household at all, with an error naming a
      // household they've never heard of. Roll again instead; three attempts is already
      // far beyond what probability asks for.
      let newCode = genCode();
      const fresh = defaultState();
      fresh.ownerEmail = ownerEmail;
      // LB-337: captured on the same form, written on the same creating save. Unlike
      // ownerEmail this is not sealed by the worker afterwards — see the save handler for
      // why a name is not treated as a credential — so Settings can edit it later with an
      // ordinary save rather than a confirm-link loop.
      fresh.ownerName = cleanOwnerName(ownerName);
      // LB-339: the household's first adult, built here rather than left to the worker's
      // migration, because this session never reads the household back — it keeps the copy
      // it just posted (below), so a migration that runs on READ would never reach it and
      // the wizard's PIN step would have nobody to set a PIN for. Same id the migration
      // uses, so a household created today and one migrated tomorrow look identical.
      fresh.adults = [newOwnerAdult({ name: fresh.ownerName, email: ownerEmail })];
      fresh.settings.ownerAdultId = fresh.adults[0].id;
      // Consent record: proof of what was agreed to and when, per the checkbox on the
      // creation screen — never overwritten by later saves (see defaultState()/migrateState()
      // handling elsewhere), so it stays the original acceptance evidence for this household.
      fresh.legal = { termsVersion: TERMS_VERSION, privacyVersion: PRIVACY_VERSION, acceptedAt: new Date().toISOString(), acceptedBy: ownerEmail };
      // W2.5 attribution (§3.3): whichever marketing source brought this visitor, made
      // permanent at the moment they convert. PRODUCT data, not analytics — exact and
      // joinable to real outcomes ("which channel brought households that reached week
      // 4?"). Absent entirely for direct visits.
      const acquisition = getAcquisition();
      if (acquisition) fresh.settings.acquisition = acquisition;
      // LB-184: the creation ticket from the invite redeem rides the creating save and is burned
      // by it. A collision (err.gone) is answered by the worker BEFORE it reads the ticket, so
      // the re-roll below still holds a live one.
      const creationTicket = readCreationTicket();
      for (let attempt = 0; ; attempt++) {
        try {
          await apiSet(newCode, fresh, null, null, null, { creationTicket });
          break;
        } catch (err) {
          if (err && err.gone && attempt < 2) { newCode = genCode(); continue; }
          throw err;
        }
      }
      clearCreationTicket();
      resetSyncTracking(fresh);
      // W2-PR(ii): mint this device's API token straight away — the creating device
      // knows the fresh passcode (defaultState generated it locally), which is exactly
      // the proof the mint endpoint wants. LB-245 (7 Oct): AWAITED, not fire-and-forget.
      // The creating save needs no token (the worker's creation gate takes the ticket), but
      // every save after it is gated, and under REQUIRE_DEVICE_TOKEN=1 the first autosave or
      // gated read could race a mint still in flight and 401. A failed mint is still
      // non-fatal: with enforcement off the next GET auto-mints as before.
      await apiMintDeviceToken(newCode, { passcode: fresh.settings.householdPasscode }).catch(() => false);
      localStorage.setItem(DEVICE_CODE_KEY, newCode);
      try { localStorage.setItem(CREATOR_KEY_PREFIX + newCode, "1"); } catch {}
      setJustCreated(true);
      markInvitePassed();
      setCode(newCode); setState(fresh); setStage("ready");
      // LB-64: the same first-touch params ride the signup event so the admin tile can
      // group signups by source without reading household records.
      track("household_created", acquisition ? { source: acquisition.source, medium: acquisition.medium || undefined, campaign: acquisition.campaign || undefined } : {});
      // Fire-and-forget: the household is already created and usable even if the email
      // fails to send (e.g. Resend hiccup), so don't block onboarding on it.
      fetch(apiUrl(`/api/household/${newCode}/welcome-email`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: ownerEmail }),
      })
        .then((res) => { if (!res.ok) showToast("Household created, but the welcome email couldn't be sent", "bad"); })
        .catch(() => showToast("Household created, but the welcome email couldn't be sent", "bad"));
    } catch (err) {
      if (err && err.inviteRequired) { onInviteRequired(); return; }
      setOnboardError(`Couldn't set up household: ${err.message}`);
    } finally { setBusy(false); }
  }

  // LB-341 — the joining device's half of the ticket flow (F2's link, F3's approval).
  //
  // Park the request, wait for the issuing adult, then mint. The mint checks the APPROVAL,
  // not the code, so this loop cannot be short-circuited by holding the link: the worst a
  // stolen code does is make a request somebody has to look at and refuse.
  // Keyed on the ticket alone. This effect sets ticketJoinState itself, so naming that state
  // in the dependency array made it tear its own loop down one tick after starting it: the
  // re-run bailed at the "asking" guard, the cleanup set live = false, and the interval —
  // declared inside the async block, out of the cleanup's reach — kept fetching for ever
  // while every tick returned early. Polling, 200s, a screen that never moved.
  useEffect(() => {
    if (!ticketJoin || startedJoinRef.current === ticketJoin.ticket) return undefined;
    if (ticketJoinStateRef.current !== "asking") return undefined;
    startedJoinRef.current = ticketJoin.ticket;
    let live = true;
    let poll = null;
    (async () => {
      // A fresh identity BEFORE the request is parked, for the reason acceptAdultInvite
      // already gives: an approved ticket is proof this device belongs here, INCLUDING a
      // device that was removed from this household and is now being let back in. A revoked
      // device id is refused by mintDeviceToken for ever (revocation beats minting, always),
      // and the id lives in localStorage — so every retry reused the same dead id and the
      // worker answered "This device was removed from the household", which the screen
      // rendered as "That code has run out". Alan, staging, 22 Sep, 3 attempts out of 3; the
      // identical dead end on the invite route was fixed this way and the ticket route was
      // never given the same treatment.
      //
      // Before apiRequestJoin, not after: the pending record stores this deviceId and the
      // mint checks joinPending.deviceId === deviceId, so regenerating later would fail that
      // check instead — or need a second approval from whoever sent the link.
      //
      // Unconditional, as on the invite route. A device holds ONE household code
      // (DEVICE_CODE_KEY), so there is no other membership for a new id to orphan; the cost
      // is a stale row in Settings - Devices when an existing member re-opens a ticket link.
      // The stale token goes too: a token minted under the previous id cannot verify under
      // the new one.
      regenerateDeviceId();
      clearDeviceToken(ticketJoin.code);
      const asked = await apiRequestJoin(ticketJoin.code, ticketJoin.ticket);
      if (!live) return;
      if (!asked.ok) { setTicketJoinState("gone"); return; }
      setTicketJoinState("waiting");
      // Polled rather than pushed: the approving device may be anywhere, and a parent who
      // taps Approve expects the other phone to move within a few seconds, not on next load.
      // One redeem at a time. The interval keeps running across the await below, so without
      // this a slow mint would have the next tick start a second one against the same ticket.
      let minting = false;
      // The mint DELETES the pending request on its way out — that is what makes the ticket
      // single-use. So a status read issued while a mint was in flight resolves to "gone"
      // AFTER it, reporting a join that worked as a dead code (Alan, staging, 22 Sep: the
      // laptop said approved, the iPad said the code had run out, three times running). The
      // `minting` guard did not catch it because the guard is checked after the await, by
      // which time the mint had finished and cleared the flag.
      //
      // The window is NOT "a slow mint" — that was the first reading, and it is too narrow.
      // Each tick issues its status read BEFORE testing the guard, so once the round trip
      // takes longer than the 3s interval there are two or three reads in flight at any
      // moment, and it is enough for ONE of them to be delivered after the mint. The
      // condition is status RTT + mint RTT > 3s, which on a phone on weak wifi is ordinary
      // rather than exceptional. Both shapes are pinned in check-invite.mjs.
      //
      // Once a mint has succeeded there is no status left worth reading, so the latch is on
      // the success rather than on any timing assumption.
      let joined = false;
      poll = setInterval(async () => {
        const status = await apiJoinRequestStatus(ticketJoin.code, asked.requestId);
        if (!live || minting || joined) return;
        if (status === "gone") { clearInterval(poll); setTicketJoinState("gone"); return; }
        if (status !== "approved") return;
        // NOT cleared here. The mint is the step that can fail for a reason that will not
        // still be true in three seconds — KV lag between the approving device's write and
        // this read is the common one — and clearing first meant a single such failure put
        // the screen back on "Waiting to be approved" with no loop left to leave it. The
        // wait was permanent and nothing on screen said so. The loop is what retries, so it
        // lives until the mint has actually worked.
        minting = true;
        const minted = await apiRedeemJoinTicket(ticketJoin.code, ticketJoin.ticket);
        minting = false;
        if (!live) return;
        if (!minted.ok) {
          // `waiting` means try again — the approval stands and the poll is still alive, so
          // the next tick does. Anything else is terminal, and only then is the loop over.
          if (!minted.waiting) { clearInterval(poll); setTicketJoinDetail(minted.error || null); setTicketJoinState("gone"); }
          return;
        }
        joined = true;
        clearInterval(poll);
        // Through a ref, not a dependency. Naming the function in the dep array would re-run
        // this effect on every render (it is redeclared each time), which would park a second
        // join request on every keystroke elsewhere in the tree. Same reason the save and
        // revoke effects above hold their callbacks at arm's length.
        // LB-341: F1 already answered "whose device is this?", so this device asks nothing.
        // forOwnDevice true means the issuing adult's own phone — Parent Mode, their PIN;
        // false (and a ticket with no issuing adult) means a shared or a child's device,
        // which is the same answer the old "A shared family device" option gave.
        const role = minted.forOwnDevice && minted.ownerAdultId
          ? { kind: "parent", adultId: minted.ownerAdultId }
          : { kind: "shared" };
        joinAdoptRef.current = { code: ticketJoin.code, deviceRole: role };
        // Three goes over about seven seconds. A device that has just been written into the
        // registry may not be visible to the very next read from the same edge, and the
        // whole gap is seconds — so the first failure is worth outwaiting before showing a
        // person anything at all.
        for (let attempt = 0; attempt < 3; attempt += 1) {
          if (!live) return;
          if (await adoptRef.current(ticketJoin.code, { deviceRole: role })) return;
          if (attempt < 2) await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
        }
        if (live) setTicketJoinState("failed");
      }, 3000);
      if (!live) clearInterval(poll);
    })();
    return () => { live = false; if (poll) clearInterval(poll); };
  }, [ticketJoin]);

  // The tail of handleJoin, minus the passcode gate and the mint — a ticket-minted device
  // already holds its token by the time this runs, so re-proving would be asking it for a
  // credential the whole feature exists to avoid.
  const adoptRef = useRef(null);
  adoptRef.current = adoptHouseholdOnThisDevice;

  async function adoptHouseholdOnThisDevice(inputCode, { deviceRole = null } = {}) {
    try {
      const value = await apiGet(inputCode);
      const data = value ? migrateState(JSON.parse(value)) : defaultState();
      resetSyncTracking(data);
      if (applyVaultInterest(data)) data._meta = { revision: savedRevRef.current + 1, lastModified: nowIso() };
      if (!value) { await apiSet(inputCode, data); resetSyncTracking(data); }
      localStorage.setItem(DEVICE_CODE_KEY, inputCode);
      // B4 has already asked whose device this is; the LB-341 ticket route has not. See
      // rememberDeviceRole.
      if (deviceRole) rememberDeviceRole(inputCode, deviceRole, data.profiles);
      else { try { localStorage.setItem(PENDING_DEVICE_SETUP_KEY_PREFIX + inputCode, "1"); } catch {} }
      markInvitePassed();
      setTicketJoin(null); setTicketJoinState(null); setTicketJoinDetail(null);
      setCode(inputCode); setState(data); setStage("ready");
      track("household_joined", {});
      return true;
    } catch (err) {
      // NOT "gone". By the time this runs on the ticket route the mint has already
      // succeeded: this device holds a token and the household has a record of it, and the
      // ticket is spent — so "ask for a new one" is the one instruction that cannot help.
      // What failed is the first read of the household, and the common reason is KV lag
      // between the mint's write and this read seeing it. Principle 5: the app does not hand
      // the parent a reason that is not true.
      // Which SCREEN follows is the caller's call, not this function's — a first failure here
      // is usually lag, so the callers outwait it rather than flashing an error over a join
      // that is about to finish, and only give up once the retries are spent.
      setTicketJoinDetail(err && err.message ? String(err.message).slice(0, 160) : null);
      return false;
    }
  }

  // B2 — read the invite before asking the person for anything. A link that has expired or
  // been used says so here rather than after they have chosen a PIN.
  useEffect(() => {
    if (!adultInvite || inviteStage !== "loading") return undefined;
    let live = true;
    (async () => {
      const preview = await apiPeekInvite(adultInvite.code, adultInvite.token);
      if (!live) return;
      if (!preview) { setInviteStage("gone"); return; }
      setInvitePreview(preview);
      setInviteStage("confirm");
    })();
    return () => { live = false; };
  }, [adultInvite, inviteStage]);

  // B3/B4 — accept with the PIN they chose and whose device this is.
  async function acceptAdultInvite(pin, ownDevice) {
    setInviteStage("accepting");
    // A fresh identity before the accept, for the same reason handleJoin gives one after a
    // correct passcode: an accepted invite is proof this device belongs here, INCLUDING a
    // device that was removed from this household and is being invited back. Carrying the
    // old, permanently-revoked device id into the accept is what produced Alan's dead end —
    // "you've been removed", then "that invite has run out". The stale token goes with it:
    // a token minted under the previous id is a guaranteed verify failure under the new one.
    regenerateDeviceId();
    clearDeviceToken(adultInvite.code);
    const res = await apiAcceptInvite(adultInvite.code, adultInvite.token, { pin, ownDevice });
    if (!res.ok) { setInviteStage(res.gone ? "gone" : "confirm"); return res; }
    const role = { kind: ownDevice ? "parent" : "shared" };
    joinAdoptRef.current = { code: adultInvite.code, deviceRole: role };
    // Same shape as the ticket route's tail, and the same truth: the accept has already run,
    // so this device is in the household whether or not the first read came back. It used to
    // fall through adopt's catch into the ticket screen's "That code has run out" — an
    // invite, reported as a code, for a join that had worked.
    const adopted = await adoptRef.current(adultInvite.code, { deviceRole: role });
    setAdultInvite(null); setInviteStage(null);
    if (!adopted) { setTicketJoinState("failed"); return res; }
    // The install offer belongs HERE and nowhere earlier. It used to be the first thing an
    // invited adult saw, because the accept screen never mounted and they landed on the
    // onboarding pitch with its install sheet over the top — an offer to install an app
    // they had not yet joined anything with. Offered once, after B4, over the app they now
    // have. InstallExplainerCard renders null where install isn't genuinely offerable.
    setOfferInstall(true);
    return res;
  }

  async function handleJoin(inputCode, inputCredential, viaToken = false) {
    setBusy(true); setOnboardError(""); setJustCreated(false);
    try {
      // The passcode/token gate runs BEFORE any attempt to fetch/adopt the household's
      // data — a wrong guess (or a code that doesn't exist at all) never gets past this, and
      // the household's own GET endpoint doesn't get touched by an unauthenticated caller.
      // See the "Household Join Passcode" scoping doc for why this is a separate check
      // rather than folding it into apiGet; the quick-join token (Aug 2026) is the exact
      // same shape, just a different secret for a device that scanned a QR instead of
      // typing the passcode by hand.
      const valid = viaToken ? await apiVerifyJoinToken(inputCode, inputCredential) : await apiVerifyPasscode(inputCode, inputCredential);
      if (!valid) {
        // Deliberately one unified message for "wrong passcode" and "no such household" —
        // not revealing which, same reasoning a login form avoids confirming account
        // existence.
        setOnboardError(
          viaToken
            ? "That QR code didn't match a household. Ask for a fresh one, or join with the household code and passcode instead."
            : "That household code and passcode don't match. Double-check both. You'll find them in Parent Mode → Settings → Household on a device that's already in."
        );
        return;
      }
      // A correct passcode is proof this device belongs here — including a device that
      // was previously kicked out and is now rejoining. Give it a fresh identity before
      // the household GET below, so it isn't still carrying an old, permanently-revoked
      // device id from before. See regenerateDeviceId() for the full reasoning.
      regenerateDeviceId();
      // HOTFIX 29 Aug: the fresh identity must not carry the OLD attempt's token. A failed
      // or interrupted earlier join can leave a stored token whose hash lives under the
      // previous deviceId — sending it with the new id is a guaranteed verify failure, so
      // every RETRY join 401'd until the failure itself cleared the token. This is the bug
      // behind "join failed in incognito but worked on the next attempt".
      clearDeviceToken(inputCode);
      // LB-245 (7 Oct, found on staging under REQUIRE_DEVICE_TOKEN=1): MINT FIRST, and wait
      // for it. The credential that just got this device in is the mint proof — quick-join
      // QR carries the join token, manual join the passcode. The mint used to be
      // fire-and-forget AFTER the household GET, so under enforcement the GET went out with
      // no token, got the gate's 401, and the client read that as a removed device ("This
      // device needs to rejoin the household") — on the very join that was rejoining it.
      // A failed mint is not fatal here: with enforcement off the GET below auto-mints as it
      // always has; with it on, the GET's own 401 is the honest answer and lands on the same
      // join screen with its message.
      await apiMintDeviceToken(inputCode, viaToken ? { joinToken: inputCredential } : { passcode: inputCredential }).catch(() => false);
      const value = await apiGet(inputCode);
      const data = value ? migrateState(JSON.parse(value)) : defaultState();
      resetSyncTracking(data);
      if (applyVaultInterest(data)) data._meta = { revision: savedRevRef.current + 1, lastModified: nowIso() };
      if (!value) { await apiSet(inputCode, data); resetSyncTracking(data); }
      localStorage.setItem(DEVICE_CODE_KEY, inputCode);
      // Every device that JOINS gets the setup flow — a brand-new household (handleStart,
      // above) never sets this, so the creator's own wizard flow is untouched. App.jsx
      // reads this once, right at mount, then clears it.
      //
      // The `profiles.length > 1` condition that used to guard this is gone (Alan, 30 Aug
      // 2026). A one-child household has nothing to hide, but it still has a device that is
      // either the child's, the family's, or a grown-up's — and that answer decides what
      // this device opens into and who gets alerts here.
      try { localStorage.setItem(PENDING_DEVICE_SETUP_KEY_PREFIX + inputCode, "1"); } catch {}
      markInvitePassed();
      setCode(inputCode); setState(data); setStage("ready");
      track("household_joined", {});
    } catch (err) {
      setOnboardError(err.rateLimited ? err.message : `Couldn't join that household: ${err.message}`);
    } finally { setBusy(false); }
  }

  // W1.7 follow-up (production-gate red, Aug 2026): `opts.auto` marks a state change as
  // MACHINE-initiated (badge sweeps, queue flushes) rather than a user edit. Auto updates
  // skip the lastLocalEditRef stamp, which two things read: the 4s refetch guard, and —
  // critically — offline dirty detection. Without this, App's kid-mode badge sweep (which
  // used to call update() even when it awarded nothing) stamped "user edited" at boot, so
  // an UNTOUCHED offline device counted as dirty and its reconnect PUSHED the stale cache
  // over whatever another device had saved in the meantime. Automatic updates must never
  // be able to claim "the user was active here".
  // An updater may return `null` to declare "nothing changed": the state object is kept
  // AS-IS (same reference), so no revision bump, no lastModified stamp, and no autosave
  // fires. This lets conditional sweeps (like the badge check) decide against the FRESH
  // state inside the updater — where React guarantees prev is current — without paying a
  // KV write for a no-op. No pre-existing updater returns null, so this is purely additive.
  function update(fn, opts) {
    if (!opts || !opts.auto) lastLocalEditRef.current = Date.now();
    setState((prev) => {
      const next = fn(JSON.parse(JSON.stringify(prev)));
      if (next === null) return prev;
      const rev = ((prev._meta && prev._meta.revision) || 0) + 1;
      next._meta = { revision: rev, lastModified: nowIso() };
      // Outbox entry for the concurrency machinery — this exact mutator is what gets
      // replayed onto a fresh server copy if this revision loses a save race. Auto ops
      // (badge sweeps etc.) are queued too: they changed state, so a rebase must carry
      // them or the retry would silently revert them. Guarded against a double-invoked
      // updater (React dev StrictMode) by replacing rather than duplicating same-rev ops.
      const ops = pendingOpsRef.current;
      if (ops.length && ops[ops.length - 1].rev === rev) ops[ops.length - 1] = { id: ops[ops.length - 1].id, rev, fn };
      else ops.push({ id: ++opSeqRef.current, rev, fn });
      return next;
    });
  }

  // Like update(), but returns a promise that only resolves once the change has actually
  // been written to the server — for spots in the UI (like the age "commit" tick) where the
  // person needs real confirmation the save landed, rather than trusting the normal
  // fire-and-forget background sync effect. Computes the next state directly from the
  // current `state` closure (not React's async setState) so the exact object being saved
  // is known immediately, with no risk of racing a stale read.
  async function updateAndSave(fn) {
    lastLocalEditRef.current = Date.now();
    const base = stateRef.current || state;
    const next = fn(JSON.parse(JSON.stringify(base)));
    if (next === null) return true; // no-op convention (see update() above) — nothing to save
    const rev = ((base._meta && base._meta.revision) || 0) + 1;
    next._meta = { revision: rev, lastModified: nowIso() };
    const op = { id: ++opSeqRef.current, rev, fn };
    pendingOpsRef.current.push(op);
    setState(next);
    stateRef.current = next; // keep the ref hot so the chained save sees this immediately
    // Save through the same single-flight chain as the background effect (which this
    // setState also triggers — the duplicate link skips itself via the revision check).
    // "True" still means what it always meant: this exact change is confirmed persisted —
    // either directly, or replayed onto a newer server copy and persisted there.
    for (let attempt = 0; attempt < 3; attempt++) {
      const res = await scheduleSave();
      if (res && res.revoked) return false;
      if (droppedOpIdsRef.current.has(op.id)) { droppedOpIdsRef.current.delete(op.id); return false; }
      if (!pendingOpsRef.current.some((o) => o.id === op.id)) return true; // confirmed (pruned on save)
      if (!(res && res.conflict)) return false; // network-class failure — background sync keeps trying, but no confirmation to give
    }
    return false;
  }

  // LB-382: in front of the gate AND of onboarding. A returning visitor whose browser still
  // holds an old invite pass but no household would otherwise be shown the create screen.
  // "I do not have Little Bankers yet" drops them onto whatever they would have seen anyway.
  if (sharedListCode) {
    return (
      <>
        <Toast toast={toast} />
        <HomeworkFork listCode={sharedListCode} onNoApp={() => setSharedListCode(null)} onGetApp={passGateWithSharedList} />
      </>
    );
  }

  // Native Run A (Part 4): the app build has no marketing page and, for now, no way to create
  // a household — that depends on the invite and waitlist flow, which needs its own design. A
  // device with no household opens on the join screen: household code and passcode, a scanned
  // or opened join link (pendingQrJoin), or the ticket and invite flows above. The same
  // joinOnly screen the landing page's "I've already got a code" route renders below, so this
  // is not a second join path. Sits in front of the web branch, which is untouched.
  if (!inviteOk && IS_NATIVE) {
    return (
      <>
        <Toast toast={toast} />
        <Onboarding onStart={handleStart} onRestore={handleRestore} onJoin={handleJoin} busy={busy} error={onboardError} pendingQrJoin={pendingQrJoin} onDismissQrJoin={() => setPendingQrJoin(null)} initialTab="join" joinOnly />
      </>
    );
  }
  if (!inviteOk) {
    // signInDirect renders the ordinary Onboarding screen on its Join tab WITHOUT setting
    // inviteOk: a returning parent proves themselves with the household code + passcode,
    // which is a stronger claim than an invite code, while the create path stays behind
    // the gate exactly as before. Back returns to the landing page.
    if (signInDirect) {
      return (
        <>
          <Toast toast={toast} />
          <Onboarding onStart={handleStart} onRestore={handleRestore} onJoin={handleJoin} busy={busy} error={onboardError} pendingQrJoin={pendingQrJoin} onDismissQrJoin={() => setPendingQrJoin(null)} initialTab="join" joinOnly onWaitlist={() => goToWaitlist("signin")} />
        </>
      );
    }
    // LB-361: `invite` is set only for a visitor who arrived on a shared link. Nothing is
    // validated until they press Get started (submitInvite, the gate's own path).
    if (!showGate) return <LandingPage onHaveCode={() => { setFocusWaitlist(false); openGate(); }} onSignIn={() => { try { window.history.pushState({ lbGate: true }, ""); } catch {} setSignInDirect(true); }} waitlistConfirmed={waitlistConfirmed} focusWaitlist={focusWaitlist}
      invite={sharedInvite ? { code: inviteInput, onCodeChange: setInviteInput, onSubmit: submitInvite, busy: inviteBusy, error: inviteError } : null} />;
    return <InvitationGate value={inviteInput} onChange={setInviteInput} onSubmit={submitInvite} error={inviteError} busy={inviteBusy} notice={inviteExpired} onBack={closeGateViaHistory} onWaitlist={() => goToWaitlist("gate")} />;
  }

  if (stage === "checking") return <div className="min-h-screen min-h-[100dvh] flex items-center justify-center text-slate-400" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)" }}>Loading…</div>;
  // LB-342 (B2, B3, B4) — in front of everything, because a person who followed an emailed
  // invite has no business seeing a join screen asking for a code they were never given.
  if (inviteStage) {
    return (
      <>
        <Toast toast={toast} />
        {/* The reload the install button performs lands HERE, and until 20 Sep this branch
            was the one place that did not render the instruction — so the page came back to
            B2 with nothing explaining why, which reads as a loop rather than a step (Alan).
            iOS has no programmatic install: the reload exists only to get a parameterised
            manifest in front of Safari, and this modal is what tells the person to finish it
            themselves with Share, then Add to Home Screen. */}
        <InstallInstructionModal open={installPromptRef.current} />
        <AcceptInvite
          stage={inviteStage}
          preview={invitePreview}
          onAccept={acceptAdultInvite}
          onGiveUp={() => { setAdultInvite(null); setInviteStage(null); }}
          /* WITHOUT THIS the install step's button has no invite to carry, falls through to
             the pre-household branch and writes resume-invite=1 — a creation pass, for the
             one person who does not need one. The icon then launches with no household and
             the PWA asks for the OWNER's PIN. Everything downstream of this line was right;
             the line itself went missing (Alan, 20 Sep). */
          adultInvite={adultInvite}
        />
      </>
    );
  }
  // LB-341 — the device that opened a ticket link, waiting on F3. It sits in front of the
  // join screen rather than beside it because there is nothing for this person to type: the
  // code did its work the moment they opened the link, and what happens next is somebody
  // else's tap. Principle 5 — a wait the app caused is stated as a wait, not as an error.
  if (ticketJoinState && ticketJoinState !== "done") {
    const gone = ticketJoinState === "gone";
    // "failed" is the third outcome, and the reason it exists: the approval landed and this
    // device was let in, but the first read of the household did not come back. The code is
    // spent, so a new one is no use, and nothing here is the parent's doing — the one useful
    // thing on this screen is the button that tries the same step again.
    const failed = ticketJoinState === "failed";
    return (
      <>
        <Toast toast={toast} />
        <div className="min-h-screen min-h-[100dvh] flex items-center justify-center p-6 bg-slate-50" data-testid="ticket-join-wait">
          <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-200 p-5 text-center">
            <h1 className="text-lg font-bold text-slate-800 mb-1">
              {failed
                ? ct("onboarding.join.ticket_failed_title", "Almost there")
                : gone ? ct("onboarding.join.ticket_gone_title", "That code has run out") : ct("onboarding.join.ticket_wait_title", "Waiting to be approved")}
            </h1>
            <p className="text-sm text-slate-500 mb-4">
              {failed
                ? ct("onboarding.join.ticket_failed_body", "This device was approved, but the household did not finish loading. Try again.")
                : gone
                  ? ct("onboarding.join.ticket_gone_body", "It may have been used already, or the 24 hours are up. Ask for a new one.")
                  : ct("onboarding.join.ticket_wait_body", "Ask whoever sent you the link to approve this device. You can leave this screen open.")}
            </p>
            {failed && (
              <button
                className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 mb-2"
                data-testid="ticket-join-retry"
                disabled={ticketJoinRetrying}
                onClick={async () => {
                  const again = joinAdoptRef.current;
                  if (!again) return;
                  setTicketJoinRetrying(true);
                  await adoptHouseholdOnThisDevice(again.code, { deviceRole: again.deviceRole });
                  setTicketJoinRetrying(false);
                }}
              >{ct("onboarding.join.ticket_failed_retry", "Try again")}</button>
            )}
            <button
              className="w-full bg-slate-100 text-slate-600 font-bold text-sm rounded-xl py-2.5"
              data-testid="ticket-join-manual"
              onClick={() => { setTicketJoin(null); setTicketJoinState(null); setTicketJoinDetail(null); }}
            >{ct("onboarding.join.ticket_manual", "Join with a code and passcode instead")}</button>
            {ticketJoinDetail && (failed || gone) && (
              <p className="text-[11px] text-slate-400 mt-3 break-words" data-testid="ticket-join-detail">{ticketJoinDetail}</p>
            )}
          </div>
        </div>
      </>
    );
  }
  if (stage === "onboard") {
    return (
      <>
        <Toast toast={toast} />
        <InstallInstructionModal open={installPromptRef.current} />
        {/* The pitch screen is where an invited adult with no app installed arrives, and its
            install sheet is the first one they see — it must carry the invite, not a creation
            pass (Alan, 20 Sep). Null for every ordinary visitor. */}
        {/* Native Run A: joinOnly in the app build — create is hidden for now (see the
            !inviteOk branch above); the invite pass this device may hold changes nothing. */}
        <Onboarding onStart={handleStart} onRestore={handleRestore} onJoin={handleJoin} busy={busy} error={onboardError} pendingQrJoin={pendingQrJoin} onDismissQrJoin={() => setPendingQrJoin(null)} adultInvite={adultInvite} joinOnly={IS_NATIVE} />
      </>
    );
  }
  return (
    <>
      <Toast toast={toast} />
      <InstallInstructionModal open={installPromptRef.current} />
      {/* The code is what makes the installed icon sign in: InstallButton issues a handover
          ticket against it and carries resume + t. Without it the card takes the
          pre-household branch and carries a creation pass instead, which is nothing this
          device needs and nothing it can use (Alan, 20 Sep). */}
      {offerInstall && <InstallExplainerCard code={code} onDismiss={() => setOfferInstall(false)} />}
      <App onSoftRefresh={() => softRefreshRef.current && softRefreshRef.current()} state={state} update={update} updateAndSave={updateAndSave} code={code} showToast={showToast} setPendingVerifiedPin={setPendingVerifiedPin} setPendingVerifiedPasscode={setPendingVerifiedPasscode} setPendingVerifiedJoinToken={setPendingVerifiedJoinToken} justCreated={justCreated} offline={offline} onLeaveHousehold={() => { clearCachedHousehold(code); localStorage.removeItem(DEVICE_CODE_KEY); setCode(null); setState(null); setStage("onboard"); }} />
    </>
  );
}

/* ------------------------------- app preview -------------------------------- */

// Lets the WHOLE app (Home/Earn/Rewards in Kid Mode, Home/Kids/Requests/Settings in
// Parent Mode — everything past onboarding) be reviewed with no real household, no Worker
// calls, and no KV writes: renders the exact same <App> component wired to local, throwaway
// React state instead of `state`/`update` from Root. Two seeded children (varied goals,
// streaks, activities, rewards) so every screen has real-looking content. Parent Mode has
// no PIN in preview (pinConfigured stays false) so it's reachable with one tap. Reached via
// littlebankers.uk/?preview=app — separate from ?preview=wizard, which only covers the
// setup-wizard steps. Photo/avatar uploads will fail here (no real /api/household/:code
// endpoint to hit) since that needs the real R2-backed Worker; everything else works.

export function buildPreviewState() {
  const s = defaultState();
  s.onboardingDone = true;
  // Vault Interest seeded explicitly (house rule: preview state never passes through
  // migrateState, so every new state field gets seeded here). Disabled by default so the
  // twenty existing specs see zero change; 21-vault-interest.spec.js enables it through
  // the real Settings UI, and the `?mm=1` hook below (AppPreview) covers the Money Monday
  // celebration path, which needs a week to have "passed".
  s.settings.vaultInterest = { enabled: false, apr: 10 };
  // The word bank catch-up has nothing to do in preview (house rule: seed every new field).
  s.wordbankBackfill = 1;
  // capMode seeded explicitly (same house rule — preview state never passes through
  // migrateState). "perChild" because the two preview kids carry DIFFERENT caps below,
  // which is the shape the guardrail specs need: identical caps would hide every
  // sized-to-their-own-cap behaviour (copies, drift, per-child suggestions) behind
  // numbers that happen to match.
  s.settings.capMode = "perChild";
  // LB-370: seeded explicitly (house rule). null so the Home card's "has this household
  // shared?" question is answered the way a real new household answers it.
  s.settings.referralSharedAt = null;
  // LB-13: seeded explicitly (house rule). "manual" so every pre-existing spec keeps the
  // request-based flow; ?preview=app&hr=auto flips it for spec 49.
  s.settings.habitRewardMode = "manual";
  // LB-248: seeded explicitly. On, like production; the ?dd=1 hook below forces TODAY to be
  // Alex's double day (the real hash lands once a month, useless to a spec).
  s.settings.doubleTokenDay = true;
  // LB-265: seeded explicitly (house rule — preview state never passes through
  // migrateState, so every new state field gets seeded here). 18:00, the shipped default,
  // so spec 11 sees the same starting chip a real household does.
  s.settings.taskReminderHour = 18;
  // LB-19: the household Adaptive switch, seeded explicitly (house rule: preview state
  // never passes through migrateState, so every new state field gets seeded here — this
  // one was missed when AdaptiveCard landed). On, like production. Note for spec authors:
  // with Adaptive on, ChildView hands the Daily Question a pre-drawn vocab pool, so a
  // spec cannot recompute the day's question with a null pool and expect a match — spec
  // 13 pins ?ad=0 for its deterministic keypad flow.
  s.settings.adaptive = true;
  // LB-316: seeded explicitly (house rule — preview state never passes through
  // migrateState, so every new state field gets seeded here). A FIXED zone rather than
  // the runner's own, so a spec or a mounted walk reads the same string in London, in CI
  // and on a laptop in another country. defaultState() has already put the real device's
  // zone here; this overwrites it on purpose.
  s.settings.timezone = "Europe/London";
  // Owner-email verification (house rule: preview state never passes through
  // migrateState, so every new state field gets seeded here). A non-null email with
  // ownerEmailVerified: false — rather than defaultState()'s own false/null pair — so
  // 38-w2-access.spec.js can exercise the unverified Settings → Household state (status
  // line, resend button, attention dot) without needing to drive the create flow first.
  s.ownerEmail = "preview-owner@littlebankers.uk";
  s.ownerEmailVerified = false;
  // LB-337 (house rule: preview state never passes through migrateState, so every new state
  // field gets seeded here). A real name rather than null, so the Settings -> Household field
  // and the email greeting are both exercisable from ?preview=app without driving create.
  s.ownerName = "Sam";
  // LB-339 (house rule: preview state never passes through migrateState, so every new state
  // field gets seeded here — and the WORKER's migration never runs for ?preview=app either,
  // since nothing is ever read back). TWO adults, not one: a single-adult household hides
  // every difference this ticket makes — the PIN pad's "Log in as", the per-adult recovery,
  // the Owner-only refusals — behind a list with nothing to choose between. Sam is the
  // Owner; Jo is a plain Adult with no permissions granted, which is what every adult in
  // release 1 is. The pins are here because preview state IS the stored state in that mode;
  // no GET ever strips them.
  // LB-342: seeded explicitly (house rule — preview state never passes through
  // migrateState, so an unseeded persisted field is silently undefined in every test).
  // Empty: a household with an outstanding invite is a state a walk drives itself into,
  // not one every spec should start in.
  s.pendingInvites = [];
  s.adults = [
    { id: "owner", name: "Sam", email: "preview-owner@littlebankers.uk", role: "owner", permissions: {}, pin: "123456", addedAt: null, addedBy: null },
    { id: "adult-jo", name: "Jo", email: "preview-jo@littlebankers.uk", role: "adult", permissions: {}, pin: "654321", addedAt: null, addedBy: "owner" },
  ];
  s.settings.ownerAdultId = "owner";
  // W2-PR(iii): seed two audit rows so preview shows the Security & History list
  // populated (house rule: every new state field gets preview coverage).
  // LB-343: adultId AND adultName on each, and deliberately one row of each shape — an
  // attributed row (G2 names the adult) and, below, an older row with nothing on it, which is
  // what every entry written before this ticket looks like. Without the second, the fallback
  // to "A parent" has no preview and no spec coverage.
  s.settingsLog = [
    { at: new Date(Date.now() - 2 * 86400000).toISOString(), deviceId: "preview-device", what: "vaultInterest.apr", from: "5%", to: "7%", adultId: "adult-jo", adultName: "Jo" },
    { at: new Date(Date.now() - 9 * 86400000).toISOString(), deviceId: "preview-device", what: "exchangeRate", from: "10 = £1", to: "5 = £1" },
  ];
  // LB-343: the preview ledger was empty, so Kid Home's activity card and the parent history
  // both had nothing to draw — which means G1 and G2 had no preview and no spec coverage at
  // all. Three rows, one of each shape the attribution chain has to handle: an approval Jo
  // resolved (G1 reads "Jo said yes"), money the Owner handed over ("Sam gave you 20 tokens"),
  // and an older row carrying neither adultId nor adultName, which is every row written before
  // this ticket and the only way to see the "A parent" fallback without inventing one.
  s.transactions = [
    { id: "preview-txn-1", deviceId: "preview-device", childId: "preview-child-1", amount: 40, reason: "Your new bike", type: "debit", balance: "token", timestamp: new Date(Date.now() - 3600000).toISOString(), requestId: "preview-req-done", adultId: "adult-jo", adultName: "Jo" },
    { id: "preview-txn-2", deviceId: "preview-device", childId: "preview-child-1", amount: 20, reason: "Helping with the shopping", type: "credit", balance: "token", timestamp: new Date(Date.now() - 2 * 86400000).toISOString(), adultId: "owner", adultName: "Sam" },
    { id: "preview-txn-3", deviceId: "preview-device", childId: "preview-child-1", amount: 5, reason: "Tidied the hallway", type: "credit", balance: "token", timestamp: new Date(Date.now() - 6 * 86400000).toISOString() },
  ];

  // ageSetAt seeded explicitly as null (buildPreviewState bypasses migrateState — every new
  // profile field gets seeded here per house rule): effectiveAge (lib/age.js) treats a
  // missing/null stamp as "age is current as-is", pinning Alex at 10 and Maya at 6 forever.
  // That stability is deliberate — Playwright specs assert on age-derived behaviour (Maya's
  // "Piggles" pig card in 12-oinks.spec.js, band/theme expectations), and a real date stamp
  // would age the preview kids out of those assertions a year from now.
  s.profiles = [
    {
      id: "preview-child-1", name: "Alex", avatar: "🦖", age: 10, ageSetAt: null, schoolYear: null, tokenBalance: 65, vaultBalancePence: 4200, earnsVaultInterest: true, vaultInterestAnchor: null, vaultInterestCarry: 0, habitThreshold: 5, habitReward: 20, lastStreakRepairWeek: null,
      spendingCap: { enabled: true, weeklyGBP: 5 },
      // LB-355: the one-off note a rate change leaves; null = nothing to show.
      pendingRateNote: null,
      goals: [
        { id: "preview-goal-1", title: "New bike", targetPence: 1200, image: null, starred: true },
        { id: "preview-goal-2", title: "Lego set", targetPence: 300, image: null },
      ],
    },
    {
      id: "preview-child-2", name: "Maya", avatar: "🦄", age: 6, ageSetAt: null, schoolYear: "Y2", tokenBalance: 18, vaultBalancePence: 1500, earnsVaultInterest: false, vaultInterestAnchor: null, vaultInterestCarry: 0, habitThreshold: 4, habitReward: 15, lastStreakRepairWeek: null,
      // £3 against Alex's £5 on purpose: a six-year-old on a smaller allowance is the
      // realistic case AND the one the spending-guardrail specs need — a linked task
      // copied between them must land on different token values.
      spendingCap: { enabled: true, weeklyGBP: 3 },
      pendingRateNote: null,
      goals: [{ id: "preview-goal-3", title: "Colouring set", targetPence: 200, image: null }],
    },
  ];
  // Spelling words are OBJECTS ({ id, word, testInclude }) exactly as the admin creates
  // them — these used to be seeded as bare strings, which SpellingTest can't actually play
  // (it reads word.word). Maya carries a small CUSTOM vocab bank with one word mastered 20
  // days ago, so R3b's spaced-repetition refresher path is visible (and testable) in
  // preview; Alex's vocab bank stays deliberately EMPTY — 13-daily-question.spec.js relies
  // on that to keep Alex's daily question always-numeric.
  const previewDaysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    const pad = (x) => String(x).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  // R5: arcade defaults are seeded here EXPLICITLY because preview state never passes
  // through migrateState() (the standing rule for every new state field) — absent would
  // still resolve to these same defaults via snackCatch.js's defensive accessors, but
  // seeding keeps preview state shaped like real migrated state.
  s.learningContent = {
    "preview-child-1": {
      spellingWords: [
        { id: "preview-word-1", word: "because", testInclude: true },
        { id: "preview-word-2", word: "friend", testInclude: true },
      ],
      vocabBank: [],
      arcade: { snackCatchEnabled: true, warmupMode: "warmup" },
      testHistory: {}, // LB-366 — seeded explicitly: preview state skips migrateState()
      homework: { lists: [] }, // LB-364 — same rule
    },
    "preview-child-2": {
      spellingWords: [],
      testHistory: {},
      homework: { lists: [] },
      arcade: { snackCatchEnabled: true, warmupMode: "warmup" },
      vocabCategory: "custom",
      vocabBank: [
        { id: "preview-vocab-1", word: "bravery", definition: "being brave when something is scary", options: ["being brave when something is scary", "running away from danger", "sleeping for a long time", "eating very quickly"], source: "custom", testInclude: true, correctCount: 0 },
        { id: "preview-vocab-2", word: "gentle", definition: "soft, kind and careful", options: ["soft, kind and careful", "loud and rough", "very fast and busy", "cold and icy"], source: "custom", testInclude: true, correctCount: 2, masteredAt: previewDaysAgo(20) },
      ],
    },
  };
  // Earning tasks are PER CHILD since the spending-guardrails work (Aug 2026) — the global
  // starter pair defaultState() used to seed is gone, so preview seeds each child's own.
  // "Tidy bedroom" is deliberately a LINKED pair across both kids at values that match
  // their different caps (Alex £5, Maya £3): that is the shape the review page's drift
  // check reads, and seeding it aligned means a spec has to actually cause drift to see
  // the flag rather than finding it already there.
  s.activities = [
    { id: "preview-act-1", label: "Read a short book", tokens: 4, childId: "preview-child-1", linkKey: null },
    { id: "preview-act-2", label: "Read a long book", tokens: 6, childId: "preview-child-1", linkKey: null },
    { id: "preview-act-3", label: "Tidy bedroom", tokens: 5, childId: "preview-child-1", linkKey: "preview-link-tidy" },
    { id: "preview-act-4", label: "Read a short book", tokens: 3, childId: "preview-child-2", linkKey: null },
    { id: "preview-act-5", label: "Tidy bedroom", tokens: 3, childId: "preview-child-2", linkKey: "preview-link-tidy" },
  ];
  s.habits = [
    { id: "preview-habit-1", label: "Brush teeth", icon: "🪥", childId: null },
    { id: "preview-habit-2", label: "Read 20 mins", icon: "📖", childId: null },
    { id: "preview-habit-3", label: "Tidy room", icon: "🧸", childId: "preview-child-1" },
  ];
  const today = todayStr();
  s.habitLog = [
    { id: "preview-log-1", childId: "preview-child-1", taskId: "preview-habit-1", date: today },
    { id: "preview-log-2", childId: "preview-child-1", taskId: "preview-habit-2", date: today },
  ];
  s.experienceRewards = [
    { id: "preview-reward-1", label: "Choose dinner", tokens: 10, childId: null },
    { id: "preview-reward-2", label: "30 mins extra screen time", tokens: 15, childId: null },
  ];
  s.requests = [
    // LB-343: a pending request carries no adult — nobody has resolved it yet. adultId and
    // adultName land on a request when it is APPROVED or REJECTED, which is what G2 draws.
    { id: "preview-req-1", childId: "preview-child-1", requestType: "earn", title: "Helped tidy the kitchen", requestedAmount: 3, status: "pending", timestamp: nowIso() },
    // W4: a completed streak week awaiting its grown-up — previews/specs the kid-side
    // nudge framing. On Maya (child-2), so Alex's tickable streak card is untouched.
    { id: "preview-req-2", childId: "preview-child-2", requestType: "habit_reward", title: "Weekly streak reward (4/4 days)", requestedAmount: 15, weekStart: weekStartOf(todayStr()), status: "pending", timestamp: nowIso() },
  ];
  return s;
}


// LB-301 (23 Sep, Alan): CmsPreviewHost is GONE from the app. The side-by-side CMS renders
// pre-generated snapshots — no iframe, no screen message — so the host listened for a message
// that could never arrive, and it kept src/lib/CmsScreenRenderer.jsx (twenty component
// namespaces, ~11.4 kB) in EVERY household's bundle. The renderer is build-time only now:
// scripts/cms-snapshots.mjs and scripts/check-screen-render.mjs import it; nothing under src/
// may (check-screen-render.mjs walks src/ and names any file that does).
//
// Claude Code, 16 Sep — kept because it stops the next person retrying it: a dynamic import
// was the obvious fix and it DOES NOT WORK HERE: the build is `esbuild --outfile` with no
// --splitting, so esbuild inlines the dynamic chunk into the same file and adds its loader
// on top. Measured: static 1,331,166 bytes, dynamic 1,334,547 — going dynamic costs 3,381
// bytes MORE. Removing the cost by splitting needs an --outdir, which changes how index.html
// and the service worker reference the bundle — a separate decision (see LB-301).

// AppPreview is mounted DIRECTLY by main.jsx and by the jsdom harnesses, so its own
// boundary has to sit here: a throw inside a state updater runs while re-rendering whoever
// owns that state, and for the preview that is AppPreviewInner. A boundary anywhere inside
// it would be below the throw. Same reasoning as main.jsx wrapping Root.
export function AppPreview(props) {
  return <AppErrorBoundary><AppPreviewInner {...props} /></AppErrorBoundary>;
}

function AppPreviewInner() {
  // Same W1.10 lesson as WizardPreview: preview routes render OUTSIDE Root, so
  // Root's own loadContentBundle() mount effect never runs for them — which is how
  // the preview wizard originally shipped without CMS content. The app preview needs
  // the bundle too (ladder bands/lists and every ct() string are bundle-driven), and
  // it's also what lets the Playwright suite drive CMS-dependent behaviour by mocking
  // /api/content (fixtures/preview.js pins it to an empty bundle so every existing
  // spec keeps exact fallback behaviour; a spec that wants content registers its own
  // route after the fixture's and wins).
  useEffect(() => {
    loadContentBundle();
  }, []);
  // `?preview=app&mm=1` — preview-only E2E hook for the Money Monday celebration (same
  // precedent as ChildView's scTest/scBoss params): enables interest at the default rate,
  // backdates Alex's anchor one week, and runs the accrual so a fresh interest transaction
  // exists on first paint. ChildView's matching mm check skips the "seed history as seen"
  // step so the overlay actually fires. Never honoured against a real household.
  const [state, setState] = useState(() => {
    const s = buildPreviewState();
    // `?preview=app&hr=auto|manual` — E2E hook for LB-13. Both drop Alex's threshold to 1 so
    // ticking his one outstanding habit ("Tidy room") completes the week on the spot; `auto`
    // then pays out instantly (65 → 85), `manual` raises the request and the nudge. Never
    // honoured against a real household.
    // `?preview=app&dd=1` — LB-248 E2E hook: marks today as the active child's double token
    // day by pinning the hashed day (settings.doubleTokenDayForce is read by nothing but the
    // preview-only override in dailyQuestion.js). Never honoured against a real household.
    // `&dd=0` (LB-321): pin the double day to a date that is never today, so the "ordinary day"
    // spec is ordinary whatever the calendar says — the hash landed on the spec's run date once.
    { const dd = new URLSearchParams(window.location.search).get("dd"); if (dd === "1") s.settings.doubleTokenDayForce = todayStr(); else if (dd === "0") s.settings.doubleTokenDayForce = "1970-01-01"; }
    // `?preview=app&ad=0` — LB-19 E2E hook: household Adaptive switch off, so the Daily
    // Question gets no adaptive vocab pool and spec 13's null-pool recompute of the day's
    // question matches what the app serves (Alex's empty bank keeps it always-numeric).
    if (new URLSearchParams(window.location.search).get("ad") === "0") { s.settings.adaptive = false; }
    // `?preview=app&coachdemo=1` — LB-92 marketing hook. Gives the two PREVIEW children a
    // month of plausible Learning Coach history so the summary, the game detail and the kid
    // strip have something true to show. This exists because the marketing screenshots have
    // to come from somewhere: a real family's data must never be photographed, and a fresh
    // preview household correctly shows empty states, which say nothing about the feature.
    //
    // Everything below is invented data about fictional children in preview mode, which is
    // never persisted and never touches KV. The dates are relative to today, so a shot taken
    // in March does not say February on it.
    // `?preview=app&sy=num` — reproduces Alan's 12 Sep crash shape: a profile whose
    // schoolYear is a NUMBER rather than "Y5". The hub called .replace() on it and took the
    // whole parent surface down; check-kid-sheet walks this so it cannot come back.
    if (new URLSearchParams(window.location.search).get("sy") === "num") {
      (s.profiles || []).forEach((p, i) => { p.schoolYear = 5 + i; });
    }
    if (new URLSearchParams(window.location.search).get("coachdemo") === "1") {
      const dayAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
      // Oldest first: the engine treats the END of the log as the most recent test.
      const log = (days, scores, total) => days.map((n, i) => ({ day: dayAgo(n), score: scores[i], total })).sort((a, b) => a.day.localeCompare(b.day));
      const mastered = (list, agoDays) => Object.fromEntries(list.map((t, i) => [t, { seen: 6, recent: [1, 1, 1], mastered: true, masteredAt: dayAgo(agoDays + i * 3) }]));
      const lc = s.learningContent;
      const seed = (childId, plan) => {
        const c = lc[childId] || (lc[childId] = {});
        c.adaptive = {
          spelling: { ...emptyGameState(plan.spellingBand), target: 3, movedUpAt: dayAgo(12), topics: mastered(plan.spellingTopics, 5), log: log([2, 4, 6, 9, 13, 17], [13, 12, 14, 11, 12, 10], 15), sessions: [{ acc: 87, abandoned: false }, { acc: 80, abandoned: false }] },
          vocab: { ...emptyGameState(plan.vocabBand), target: 2, topics: {}, log: log([3, 5, 10, 14], [9, 8, 9, 8], 15), sessions: [{ acc: 60, abandoned: false }] },
          mathsworkout: { ...emptyGameState(plan.mathsBand), target: 3, topics: {}, log: log([3, 8, 16], [7, 8, 7], 12), sessions: [{ acc: 62, abandoned: false }], practice: plan.mathsPractice },
          ttstreaks: { ...emptyGameState(null), target: plan.ttTarget, topics: mastered(plan.ttTopics, 6), log: log([1, 4, 7, 11, 15], [18, 20, 17, 16, 15], 20), sessions: [{ acc: 90, abandoned: false }] },
        };
      };
      // LB-366: a few recent tests in the history, so the lists and the detail screens have
      // something true-shaped to show (one of each state: paid, below pass, stopped, list).
      const at = (daysAgo, h, m) => { const d = new Date(); d.setDate(d.getDate() - daysAgo); d.setHours(h, m, 0, 0); return d.toISOString(); };
      const sp = (w, given) => ({ q: w, given: given ?? w, answer: w, correct: given === undefined });
      lc["preview-child-1"].testHistory = {
        spelling: [
          { id: "demo-sp-3", at: at(7, 16, 40), game: "spelling", score: 3, total: 15, answered: 4, stopped: true, source: "coach", oinks: 0, tokens: 0, sent: false, qualified: null, gateOpen: false, passMark: 75, passKind: "pct", questions: [sp("friend"), sp("because", "becos"), sp("through"), sp("answer")] },
          { id: "demo-sp-2", at: at(5, 17, 20), game: "spelling", score: 15, total: 15, answered: 15, stopped: false, source: "list", oinks: 15, tokens: 10, sent: false, qualified: "full", gateOpen: true, passMark: 75, passKind: "pct", questions: ["friend", "through", "answer", "caught", "believe", "different", "February", "library", "ordinary", "popular", "quarter", "strange", "weight", "island", "because"].map((w) => sp(w)) },
          { id: "demo-sp-1", at: at(0, 16, 12), game: "spelling", score: 13, total: 15, answered: 15, stopped: false, source: "coach", oinks: 13, tokens: 8, sent: false, qualified: "pass", gateOpen: true, passMark: 75, passKind: "pct", questions: [sp("friend"), sp("because", "becuse"), sp("through"), sp("answer"), sp("caught"), sp("believe"), sp("different"), sp("island", "iland"), sp("February"), sp("library"), sp("ordinary"), sp("popular"), sp("quarter"), sp("strange"), sp("weight")] },
        ],
        vocab: [
          { id: "demo-vo-1", at: at(2, 18, 5), game: "vocab", score: 4, total: 15, answered: 6, stopped: true, source: "coach", oinks: 0, tokens: 0, sent: false, qualified: null, gateOpen: false, passMark: 75, passKind: "pct", questions: [
            { q: "reluctant", given: "excited", answer: "not wanting to do something", correct: false },
            { q: "enormous", given: "very big", answer: "very big", correct: true },
            { q: "fragile", given: "easily broken", answer: "easily broken", correct: true },
            { q: "cautious", given: "careful to avoid danger", answer: "careful to avoid danger", correct: true },
            { q: "ancient", given: "noisy", answer: "very old", correct: false },
            { q: "vivid", given: "bright and strong", answer: "bright and strong", correct: true },
          ] },
        ],
        mathsworkout: [
          { id: "demo-mw-1", at: at(0, 15, 50), game: "mathsworkout", score: 7, total: 10, answered: 10, stopped: false, source: "coach", oinks: 7, tokens: 0, sent: false, qualified: null, gateOpen: true, passMark: 75, passKind: "pct", questions: [
            { q: "7 × 8", given: "54", answer: "56", correct: false }, { q: "36 + 47", given: "83", answer: "83", correct: true }, { q: "What comes after 199?", given: "200", answer: "200", correct: true },
            { q: "120 - 45", given: "75", answer: "75", correct: true }, { q: "9 × 6", given: "", answer: "54", correct: false, timedOut: true }, { q: "64 ÷ 8", given: "8", answer: "8", correct: true },
            { q: "15 + 28", given: "43", answer: "43", correct: true }, { q: "6 × 7", given: "", answer: "42", correct: false, skipped: true }, { q: "90 - 37", given: "53", answer: "53", correct: true }, { q: "11 × 3", given: "33", answer: "33", correct: true },
          ] },
        ],
        ttstreaks: [
          { id: "demo-tt-1", at: at(3, 10, 31), game: "ttstreaks", score: 18, total: 20, answered: 20, stopped: false, source: "coach", oinks: 18, tokens: 6, sent: false, qualified: "pass", gateOpen: true, passMark: 16, passKind: "streak", streak: 16, questions: Array.from({ length: 20 }, (_, i) => { const a = (i % 11) + 2; const b = [3, 4, 6, 7][i % 4]; const wrong = i === 0 || i === 17; return { q: `${a} × ${b}`, given: String(wrong ? a * b + 1 : a * b), answer: String(a * b), correct: !wrong }; }) },
        ],
      };
      seed("preview-child-1", { spellingBand: "y56", spellingTopics: ["silent-letters", "plurals"], vocabBand: "fluentReaders", vocabTopics: ["prefixes"], mathsBand: "strategist", mathsTopics: ["long-multiplication"], mathsPractice: ["division-remainders", "fractions", "decimals"], ttTarget: 4, ttTopics: ["table-6", "table-7"] });
      seed("preview-child-2", { spellingBand: "y34", spellingTopics: ["double-letters"], vocabBand: "developingReaders", vocabTopics: ["synonyms"], mathsBand: "builder", mathsTopics: ["times-tables-facts"], mathsPractice: ["column-addition"], ttTarget: 2, ttTopics: ["table-2", "table-5"] });
    }
    // `?preview=app&oinks=N` — gives Alex N Oinks (spec 12 asserts the plain preview starts at 0,
    // so this is a hook, not a seed). Spec 52 uses 40 so the Gifts tab has something to give.
    { const n = Number(new URLSearchParams(window.location.search).get("oinks")); if (n > 0) { const a = s.profiles.find((p) => p.id === "preview-child-1"); if (a) a.oinks = n; } }
    // `?preview=app&gift=1` — LB-247 E2E hook: Maya has just sent Alex 25 Oinks.
    if (new URLSearchParams(window.location.search).get("gift") === "1") { const a = s.profiles.find((p) => p.id === "preview-child-1"); a.pendingOinkGift = { from: "Maya", amount: 25, at: nowIso() }; }
    const hr = new URLSearchParams(window.location.search).get("hr");
    if (hr === "auto" || hr === "manual") {
      s.settings.habitRewardMode = hr;
      s.profiles.find((p) => p.id === "preview-child-1").habitThreshold = 1;
    }
    // `?preview=app&hw=finish` — homework E2E hook (Alan, prod, 29 Sep: no tokens at 6 of 6
    // known). Alex gets a two-word Latin list (English to Latin, so the prompt is on screen for
    // a test to read), each word already right on one earlier day, with 5 finishing tokens and
    // Oinks on, so one all-right go today completes it and pays.
    if (new URLSearchParams(window.location.search).get("hw") === "finish") {
      const lc = s.learningContent["preview-child-1"];
      const yday = dateStrAddDays(todayStr(), -1);
      const list = newHomeworkList({ code: "HW-PRVW01", type: "language", lang: "Latin", direction: "en2lang", name: "Latin verbs", pairs: [{ a: "I love", b: "amo" }, { a: "We love", b: "amamus" }], oinksOn: true, finishTokens: 5, until: dateStrAddDays(todayStr(), 7), createdAt: nowIso(), makeId: (i) => `hw-prvw-${i}` });
      list.progress = Object.fromEntries(list.pairs.map((p) => [p.id, { days: [yday], right: 1, wrong: 0 }]));
      lc.homework = { lists: [list] };
    }
    if (new URLSearchParams(window.location.search).get("mm") === "1") {
      s.settings.vaultInterest = { enabled: true, apr: 10 };
      s.profiles.forEach((p) => { p.vaultInterestAnchor = dateStrAddDays(weekStartOf(todayStr()), -7); p.vaultInterestCarry = 0; });
      applyVaultInterest(s);
    }
    return s;
  });
  const [toast, showToast] = useToast();

  function update(fn) {
    setState((prev) => {
      const next = fn(JSON.parse(JSON.stringify(prev)));
      if (next === null) return prev; // same no-op convention as the real update() above
      next._meta = { revision: ((prev._meta && prev._meta.revision) || 0) + 1, lastModified: nowIso() };
      return next;
    });
  }
  async function updateAndSave(fn) { update(fn); return true; }

  return (
    <>
      <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-xs font-semibold px-3 py-2 text-center relative z-50">
        Preview mode — nothing is saved. Remove <code>?preview=app</code> from the URL to exit.
      </div>
      <Toast toast={toast} />
      <App
        state={state}
        update={update}
        updateAndSave={updateAndSave}
        code="PREVIEW"
        showToast={showToast}
        setPendingVerifiedPin={() => {}}
        setPendingVerifiedPasscode={() => {}}
        justCreated={false}
        // Preview has no server to re-fetch from — the gesture is deliberately inert here.
        onSoftRefresh={() => {}}
        onLeaveHousehold={() => setState(buildPreviewState())}
      />
    </>
  );
}
